export type Language = 'en' | 'pl' | 'by';

export const translations = {
  en: {
    nav: {
      services: "Services",
      whyAgape: "Why AGAPE?",
      howWeWork: "How We Work",
      caseStudies: "Case Studies",
      resources: "Resources",
      blog: "Blog",
      contact: "Contact",
      aboutUs: "About Us",
      bookCall: "Book a Call",
    },
    hero: {
      tagline: "B2B Growth Engineers",
      headline: "Turn sales chaos into a predictable B2B meeting Engine",
      description: "AGAPE Automation Systems builds the safe, automated outreach engine that delivers predictable B2B meetings — without hiring SDRs and without burning your domain.",
      bookCall: "Book a 25-min Discovery Call",
      ourServices: "See Our Products",
    },
    services: {
      title: "What We Do",
      subtitle: "Comprehensive consulting services designed to accelerate your business growth and digital transformation.",
      learnMore: "View service details",
      items: [
        {
          title: "Business Development",
          description: "Driving growth through strategic market expansion",
          details: ["Market analysis & opportunity mapping", "Go-to-market strategy", "Sales process optimization", "Revenue growth initiatives"],
        },
        {
          title: "Digital Marketing",
          description: "Maximizing your digital presence and customer reach",
          details: ["Multi-channel campaign management", "Content strategy & creation", "Performance analytics & optimization", "Brand positioning & messaging"],
        },
        {
          title: "Interim Management",
          description: "Expert leadership when you need it most",
          details: ["C-suite & senior management roles", "Transformation leadership", "Crisis management", "Knowledge transfer & team development"],
        },
        {
          title: "AI Solutions",
          description: "Leveraging AI to transform your business operations",
          details: ["Ready-made solutions", "Custom-built solutions", "Systems & solutions integration"],
          hasButton: true,
        },
      ],
    },
    whyAgape: {
      title: "Why AGAPE?",
      subtitle: "Most agencies sell activity and hand you a tool. AGAPE works for sales results and delivers them as a full white-glove service.",
      values: [
        {
          title: "Sales, Not Vanity Metrics",
          description: "Unlike agencies that report views, clicks and sent messages, AGAPE works for the number that matters most to you — usually closed sales. No work for the sake of work, no fake statistics.",
        },
        {
          title: "White-Glove Service, Not Just Software",
          description: "Unlike tools that leave you alone with the technology, AGAPE runs its own top lead-generation system for you and delivers the result: qualified leads and meetings, not another login.",
        },
        {
          title: "Speed & Flexibility",
          description: "Unlike traditional hiring, which takes months, a typical outreach engine goes live in 14–28 days, from database preparation and messaging to safe domain setup.",
        },
        {
          title: "Measurable Impact",
          description: "Outcomes are tracked against real business goals: KubaDrukuje recorded 12 Instagram orders in three months.",
        },
        {
          title: "Strategic Focus",
          description: "For AleksanovInvest, AGAPE refined the client persona and offer before optimizing LinkedIn sales activity, which led to a first paid deal.",
        },
        {
          title: "Global Reach",
          description: "International work includes a digital platform and recurring AI workshops for the Belarusian diaspora in Krakow.",
        },
      ],
    },
    howWeWork: {
      title: "How We Work",
      subtitle: "Our proven 6-step methodology ensures consistent results and transparent collaboration throughout your journey with us.",
      cta: "Book a free 25-minute discovery call",
      ctaHeading: "Ready to start your journey with AGAPE?",
      steps: [
        {
          title: "Discovery",
          shortText: "Discovery is a 25-minute initial call to assess your business goals, challenges, and current situation and decide whether working with AGAPE is a good fit.",
          expandedText: "",
        },
        {
          title: "Deep Dive",
          shortText: "Comprehensive analysis session",
          expandedText: "A thorough 60-90 minute session where we dive deep into your specific needs, analyze your market position, and identify key opportunities for growth and improvement.",
        },
        {
          title: "Strategy",
          shortText: "Custom roadmap creation",
          expandedText: "Based on our analysis, we develop a tailored strategy with clear milestones, KPIs, and actionable recommendations designed specifically for your business context.",
        },
        {
          title: "Proposal",
          shortText: "Transparent engagement terms",
          expandedText: "You receive a detailed proposal outlining scope, timeline, deliverables, and investment. We believe in complete transparency - no hidden costs or surprises.",
        },
        {
          title: "Execution",
          shortText: "Hands-on implementation",
          expandedText: "Our team works alongside yours to implement the strategy. We provide regular updates, adapt to changing circumstances, and ensure consistent progress toward your goals.",
        },
        {
          title: "Results",
          shortText: "Measurable outcomes & growth",
          expandedText: "We measure success through concrete results: revenue growth, market expansion, operational efficiency, or whatever metrics matter most to your business.",
        },
      ],
    },
    caseStudies: {
      title: "Case Studies",
      subtitle: "Real results from real partnerships. Discover how we've helped businesses transform challenges into opportunities.",
      asOf: "All figures verified as of September 2026.",
      challenge: "Challenge",
      solution: "Solution",
      keyValue: "Key Value",
      learnMore: "Learn more",
      cases: [
        {
          company: "WorkForce PRO",
          challenge: "WorkForce PRO needed a professional online presence that would build its image as an expert in process outsourcing and attract B2B clients.",
          solution: "AGAPE developed a communications strategy, manages the company's LinkedIn account, and creates a newsletter, combining organic activities with email and LinkedIn Outreach campaigns.",
          keyValue: "Thanks to an empathetic approach and consistent communication, AGAPE built a brand from scratch, with over 400 followers, 40 posts, and a noticeable increase in interest in WorkForce PRO's offerings.",
        },
        {
          company: "VisualCore",
          challenge: "Visual Core needed to quickly launch an MVP of its cognitive architecture-based system, integrated with Telegram messaging, and ready for monetization.",
          solution: "AGAPE built a chatbot connected to a vector database (RAG) and a subscription system in two weeks. Each user session was isolated, ensuring complete security and scalability.",
          keyValue: "The collaboration resulted in a rapid sales launch and a consistent brand image on LinkedIn. AGAPE supported Visual Core in developing a marketing strategy, content plan, and educational product communication.",
        },
        {
          company: "KubaDrukuje",
          challenge: "Kuba had talent and a passion for 3D printing, but he lacked direction, strategy, and coherent brand communication. Ideas abounded, but structure and an effective plan were lacking.",
          solution: "AGAPE helped define the brand's mission, strategy, and communication style. We built a content plan, curated the offer, and took over marketing activities on Instagram.",
          keyValue: "KubaDrukuje's Instagram profile grew by 313% in three months, achieving 27,000 views and 12 orders through AGAPE's structured marketing and content strategy.",
        },
        {
          company: "Himtek",
          challenge: "HIMTEK, an industrial company, needed to increase brand visibility and generate more inquiries through LinkedIn - a channel whose potential had not been fully utilized until now.",
          solution: "AGAPE launched a corporate account, publishing two posts per week and building communication aimed at potential B2B clients. We focused on sales and branding content.",
          keyValue: "After just the first month, content optimization guidelines for conversion and a strategy for further growth were developed. HIMTEK gained an active communication channel and a solid foundation for future campaigns.",
        },
        {
          company: "DanGuitarra",
          challenge: "A guitar teacher needed to increase visibility and interest in his lessons among local students, particularly among younger age groups active on social media.",
          solution: "AGAPE developed a TikTok-based marketing strategy, prepared a content plan, scripts, and recordings with the client, and handled video editing and post copywriting.",
          keyValue: "A complete marketing and operations system was created: content, a TikTok campaign, online bookings with payments and SMS notifications. The school gained structure, automation, and online visibility.",
        },
        {
          company: "Polesie United",
          challenge: "Polesie United began as a team of friends without a strategy, structure, or recognition. It lacked consistent communication, resources, and financial support to develop into a professional project.",
          solution: "AGAPE took over the club's comprehensive marketing - from rebranding and social media to the merchandise store and content strategy. We also provided organizational support for the transition to an association and prepared a sponsorship offer.",
          keyValue: "In just one year, Polesie United went from a team of friends to a fully-fledged club with 1,000+ followers, an active community, and the first sponsor who believed in the project. AGAPE built the club's brand, structure, and future.",
        },
        {
          company: "American Food Production Company",
          challenge: "The client had over 2,000 files in a 20-year-old Google Drive archive. The lack of structure, organization, and quick access to data hampered operational work and document management.",
          solution: "AGAPE fully digitized its resources, creating a Company Operating System in Notion. Documents were segmented, annotated, and linked to content search using Notion AI.",
          keyValue: "The result was a central knowledge repository that transformed chaos into a transparent ecosystem. The client gained immediate access to 20 years of company history and a solid foundation for further automation.",
        },
        {
          company: "Natalia Pietrów",
          challenge: "Monetizing highly specialized knowledge (biohacking) required building a scalable sales infrastructure and a structured marketing strategy from scratch, without generating chaos or increasing the creator's operational costs.",
          solution: "AGAPE implemented a complete operational architecture: from defining Brand Core and USP, through designing the product structure, to the technical e-commerce infrastructure (landing page, domain, automated e-book distribution, and linking videos with QR codes).",
          keyValue: "The creator gained an automated client acquisition system and could focus 100% on the substantive content. Eliminating manual work secured a stable, predictable sales process and a steady flow of leads for the new brand.",
        },
        {
          company: "AleksanovInvest",
          challenge: "AleksanovInvest – an educational brand in the investment space – had no clearly defined brand, client persona, or coherent offer. Initial communication targeted the wrong segment, and the marketing channel was poorly chosen. The way of communicating was ineffective – delivering minimal sales results despite huge effort. The owner was also unsure what the product should look like and to whom it should actually be sold.",
          solution: "Within the first few months, AGAPE guided the client through a structured strategic process on a weekly cadence: defining the brand and persona, iterating the offer multiple times, aligning communication with the new ICP, and launching and optimizing a LinkedIn outreach campaign. In parallel, the website, consultation offer, and sales fundamentals were put in order.",
          keyValue: "The collaboration gave Roman Aleksanov firm clarity on what his product should look like, what to do to make it sell, and who to target. The LinkedIn campaign expanded the network, helped identify effective outbound messaging, and delivered conversion into the first paid deal – built on the new offer and consistent brand fundamentals. AGAPE currently supports AleksanovInvest with client acquisition.",
        },
        {
          company: "Belarusian Diaspora in Krakow",
          challenge: "The diaspora community needed not only a professional digital infrastructure built from scratch, but above all a way out of technological exclusion. There was no central communication hub and no practical knowledge of how members could leverage artificial intelligence to boost personal and business productivity.",
          solution: "AGAPE designed and deployed the complete web architecture (belkrakow.org) – from domain configuration to UI/UX. In parallel, Roman Ledak launched \"Labaratoryja Budućyni\" (The Future Lab) – a recurring workshop program teaching how to apply AI tools in everyday operations and tasks.",
          keyValue: "A stable, maintenance-free digital ecosystem was built for the community. Every month some community members gain hard skills during the workshops, directly translating AI tools into automation and optimization of their own businesses, building deep trust in AGAPE's expertise.",
        },
        {
          company: "Spotless Polska",
          challenge: "Marcin (Wrocław) was entering the market with chemical-free cleaning of photovoltaic panels and glass facades. He had the technology, but no brand, website, price list or references – he needed to test real demand, not a pretty company presentation.",
          solution: "AGAPE built the brand from scratch (domain, mailbox with reputation warm-up, website, logo and flyer designs), then assembled a database of about 1,000 companies and ran email campaigns to four segments: PV farms, car dealerships, warehouses and production plants.",
          keyValue: "743 emails reached 385 companies and brought 38 replies (9.9%) plus 4 quote requests. The client keeps four ready-to-reuse campaigns and the database – a working sales foundation, not a one-off campaign.",
        },
      ],
    },
    resources: {
      title: "Resources & Insights",
      subtitle: "Stay informed with our latest thoughts on AI, digital transformation, and business strategy.",
      readOn: "Read on LinkedIn",
      viewAll: "View All on LinkedIn",
      posts: [
        {
          title: "AI Automation & Digital Transformation",
          excerpt: "Exploring how AI-driven automation accelerates digital transformation across industries and helps businesses evolve smarter.",
        },
        {
          title: "AI Adoption in Polish Companies",
          excerpt: "How Polish businesses are embracing artificial intelligence to boost innovation and efficiency.",
        },
        {
          title: "The Human-AI Partnership",
          excerpt: "How human creativity and AI synergy redefine productivity and collaboration.",
        },
        {
          title: "Generative AI & Creativity",
          excerpt: "Exploring the rise of generative AI in design and content innovation.",
        },
        {
          title: "AI Humor & Real-World Lessons",
          excerpt: "A lighthearted take on AI errors that reveal deeper truths about innovation.",
        },
        {
          title: "Digital Hygiene Training by AGAPE",
          excerpt: "A practical workshop series on digital well-being and smarter technology use.",
        },
      ],
    },
    contact: {
      title: "Let's Start a Conversation",
      subtitle: "Ready to explore how AGAPE can help transform your business? Book a free 25-minute discovery call and let's discuss your goals.",
      bookCallTitle: "Book a Free Call",
      bookCallDesc: "Schedule a 25-minute discovery call to discuss your business needs and explore how we can help.",
      bookCallBtn: "Book via Calendly",
      contactTitle: "Contact Information",
      followUs: "Follow Us",
    },
    aboutUs: {
      heroTitle: "Unlocking Digital Potential",
      heroSubtitle: "We help businesses find calm, confidence, and real value through empathy and technological mastery.",
      corePhilosophy: "Accessible Empowerment",
      corePhilosophyDesc1: "Breaking cost and technology barriers to make innovation",
      corePhilosophyDesc2: "simple and achievable.",
      founderJourney: {
        title: "The Founder's Journey",
        subtitle: "From IBM to AGAPE",
        corporate: {
          period: "2020-2023",
          title: "Corporate Foundation",
          company: "IBM Consulting",
          role: "Automation Consultant",
          description: "Roman Ledak served as an Automation Consultant at IBM Consulting. He led dozens of end-to-end projects and assessed hundreds of processes for global leaders in finance and logistics."
        },
        commercial: {
          period: "2023-2025",
          title: "The Commercial Bridge",
          company: "Marketing Automation Agency",
          role: "Delivery Director",
          description: "As Delivery Director at a Marketing Automation Agency, Roman shifted the focus from operational savings to revenue generation, pioneering AI-driven marketing that boosted team productivity by 40%."
        },
        breakthrough: {
          period: "2025",
          title: "The AGAPE Breakthrough",
          description: "Founded to conquer the gap in the \"Post-Hiring Economy,\" allowing SMEs to scale operations and sales without the overhead of increasing headcount."
        },
        evolution: {
          period: "2026",
          title: "Evolution to AGAPE AUTOMATION SYSTEMS",
          description: "Merging the competencies of two young engineers with complementary skills and shared vision, enabling scalable value delivery."
        }
      },
      specializedEdge: {
        title: "Our Specialized Edge",
        subtitle: "Pattern Recognition",
        diagnostic: {
          title: "The Diagnostic Talent",
          description: "AGAPE excels at diagnosing exactly where value is being lost in messy, complex operations."
        },
        stability: {
          title: "Industrial-Grade Stability",
          description: "Unlike the \"hype-driven\" AI market, we provide stable, reliable automation built on corporate-grade standards."
        },
        goal: {
          title: "The Goal",
          description: "To design technology that disappears into the background, allowing you to focus on your vision."
        }
      },
      values: {
        title: "The AGAPE Values",
        subtitle: "The \"Highest Form of Love\"",
        intro: "We redefine consulting through a lens of empathy and measurable worth.",
        items: [
          {
            title: "Value Driven",
            impact: "Every action must deliver measurable, quantifiable worth for your business."
          },
          {
            title: "Sage-Innovator",
            impact: "We combine the wisdom of a guide with the energy of a creator to solve problems."
          },
          {
            title: "Empathy & AGAPE",
            impact: "We understand that business problems cause personal stress; we aim to bring you peace of mind."
          },
          {
            title: "Partnership",
            impact: "We don't just deliver; we build your team's internal capabilities and confidence."
          }
        ]
      },
      credibility: {
        title: "Why Trust Us?",
        subtitle: "Credibility Pillars",
        pillars: [
          {
            title: "Proven Results",
            description: "Millions saved for global leaders and hundreds of processes optimized."
          },
          {
            title: "Agency Strategy, SME Price",
            description: "Enterprise-level strategy at a price accessible to ambitious businesses."
          },
          {
            title: "Young & Open-Minded",
            description: "A team driven by \"outside-the-box\" thinking and creative problem-solving."
          }
        ]
      },
      cta: {
        title: "Ready to bridge the gap?",
        subtitle: "Between where you are and where you want to be?",
        button: "Book a Free 25-Minute Discovery Call"
      }
    },
    personaFi: {
      hero: {
        tagline: "AGAPE • Value Driven AI for Marketers",
        title: "Validate your marketing in",
        titleHighlight1: "2 minutes",
        titleConnector: "for",
        titleHighlight2: "€1",
        description: "Stop guessing whether your personas and messaging will work. Get framework-based validation in 2 minutes-without €5,000 agency retainers, weeks of waiting, or AI prompting expertise.",
        founderLabel: "Founder-Tested, Proven Results:",
        founderQuote: "\"Although I have solid expertise in marketing and was empowered by AI even before - I still missed perspectives. PersonaFi provided 360 degrees feedback data.\"",
        founderName: "- Roman Ledak, AGAPE Founder, Krakow",
        statReply: "+158% reply rate",
        statVisits: "+100% website visits",
        statConversion: "+30% conversion",
        ctaTry: "Try Free Now (30 sec)",
        ctaSample: "See Sample Report First",
        trustAccuracy: "At least 95% accuracy",
        trustFree: "3 runs FREE to start",
        trustRefund: "Full refund guarantee",
        trustData: "We don't use your data",
        versionNote: "PersonaFi v1.0 · as of September 2026",
      },
      stats: {
        title: "By The Numbers",
        founderTested: "FOUNDER-TESTED",
        founderTestedDesc: "Real validation, real results",
        replyRate: "+158%",
        replyRateDesc: "Reply rate improvement (12%→31%)",
        accuracy: "≥95%",
        accuracyDesc: "Accuracy verified by professionals",
        price: "€1",
        priceDesc: "Per validation - less than a coffee",
        tagline: "Real validation. Real impact. Real marketers.",
        asOf: "Data as of September 2026.",
      },
      beforeAfter: {
        title: "See PersonaFi In Action",
        subtitle: "Real examples showing how PersonaFi transforms marketing results.",
        example1Title: "Real Example: Outreach Campaign Validation",
        example1Subtitle: "Roman Ledak (AGAPE Founder) was getting 12% reply rate. Here's what PersonaFi revealed:",
        beforeTitle: "Before PersonaFi",
        beforeText: "\"Hi [Name], I help businesses improve their marketing with AI. Would you be interested in learning how we can help you?\"",
        beforeResult: "Result: 12% reply rate",
        identifiedTitle: "What PersonaFi Identified",
        identifiedScore: "Message-Persona Fit Score:",
        identifiedItems: ["No specific pain point addressed", "\"Improve marketing with AI\" too vague", "No buying trigger mentioned", "CTA assumes interest"],
        afterTitle: "After PersonaFi Revisions",
        afterText: "\"Hi [Name], saw you're launching [new service]. Most founders waste €500-€2K testing messaging that doesn't resonate...\"",
        afterReply: "31% reply rate (+158%)",
        afterVisits: "+100% website visits",
        afterConversion: "+30% conversion",
        realExample: "THIS IS A REAL EXAMPLE from PersonaFi's own founder.",
        example2Title: "Social Media Content Validation",
        example2Subtitle: "What a social media manager entered:",
        example2Input: "Persona: \"Instagram users 25-35 interested in sustainable fashion\" | Content: Instagram carousel \"10 Ways to Build a Capsule Wardrobe\"",
        example2Score: "Content-Persona Fit Score:",
        example2ScoreLabel: "3/5 (Moderate)",
        example2WhyTitle: "Why this misses:",
        example2WhyItems: ["\"Capsule wardrobe\" ≠ sustainability", "No sustainable brands or materials", "No environmental impact angle", "\"10 ways\" educates but doesn't engage"],
        example2StrongerTitle: "Stronger content angles:",
        example2StrongerItems: ["\"10 Sustainable Brands for Your Capsule Wardrobe\"", "\"How I Built a Capsule Wardrobe with Zero Fast Fashion\"", "\"Environmental Impact: 10-Item vs. 100-Item Wardrobe\""],
        example2TipsTitle: "Instagram tips:",
        example2TipsItems: ["Add poll sticker: \"Which sustainable brand next?\"", "UGC angle: Ask followers to share wardrobes"],
        ctaTryFree: "Try Free Validation",
      },
      accuracy: {
        title: "How We Measure \"At Least 95% Accuracy\"",
        subtitle: "PersonaFi achieves at least 95% accuracy when validated against experienced marketing professionals.",
        criteriaIntro: "We compare PersonaFi's reports against professional strategists (10+ years) across 6 criteria:",
        criteria: ["ICP clarity & completeness", "Pain point accuracy", "Messaging resonance", "Objection handling", "Competitive positioning", "Psychological triggers"],
        whyTitle: "Why \"at least\"?",
        whyText: "In many cases, PersonaFi identifies gaps that even experienced marketers miss. We set 95% as the minimum benchmark-actual alignment often exceeds this.",
        conclusion: "Professional-grade analysis without hiring professionals.",
      },
      howItWorks: {
        title: "How PersonaFi Works",
        subtitle: "4 simple steps. 2 minutes total. Zero technical skills required.",
        steps: [
          { title: "Paste Your Content", desc: "Share what you want validated: value proposition, email, landing page, social media.", example: "Plain text works-no special formatting." },
          { title: "Describe Your Customer", desc: "Enter 2-3 sentences about who you're targeting.", example: "\"Small business owners who struggle with marketing and don't have budget for agencies\"" },
          { title: "Get Instant Validation", desc: "Returns a structured report with scores, green flags, red flags, and concrete recommendations.", example: "Scored report with specific recommendations." },
          { title: "Iterate or Launch", desc: "Use feedback to iterate and improve your assets, or use feedback in your decks.", example: "This price allows you to conduct multiple iterations to achieve perfection level in marketing." },
        ],
        cta: "Start Free Validation Now",
      },
      whoItsFor: {
        title: "Built For Marketers Who Can't Afford To Guess",
        personas: [
          { title: "Solopreneurs & Small Teams", benefits: ["Validate messaging before spending on ads", "Get an unbiased second opinion on your pitch", "Launch with confidence, not gut feeling"] },
          { title: "Content & Marketing Managers", benefits: ["Test multiple campaign angles in one afternoon", "Build data-driven business cases for leadership", "Prioritize content that resonates with your ICP"] },
          { title: "Social Media Marketers", benefits: ["Validate social media concepts fast", "Compare multiple content angles side by side", "Optimize hooks and CTAs before publishing"] },
          { title: "Marketing Agencies", benefits: ["Bill clients €500 for analysis that costs you €1", "White-label reports as proprietary methodology", "Scale personalized insights across all accounts"] },
          { title: "Enterprise Teams (10+)", benefits: ["Validate 8+ segments in under 2 days", "Integrate insights with CRM workflows", "Align cross-functional teams around validated data"] },
        ],
      },
      testimonial: {
        title: "Real Results From PersonaFi Users",
        quote: "\"After I started using PersonaFi to validate and improve my outreach campaigns, the results were stunning. PersonaFi showed me exactly what was missing in my messaging and how to fix it. Although I have solid expertise in marketing and was empowered by AI even before - I still missed perspectives. PersonaFi provided 360 degrees feedback data.\"",
        badgeReply: "+158% reply rate",
        badgeVisits: "+100% website visits",
        badgeConversion: "+30% conversion",
        authorName: "Roman Ledak, Founder",
        authorCompany: "AGAPE • Krakow, Poland",
        invitation: "PersonaFi is newly launched. If it helps you improve your marketing, we'd love to feature your results.",
        invitationCta: "Try Free Validation - Be Part of Our Success Stories",
      },
      comparison: {
        title: "How PersonaFi Compares To Your Alternatives",
        headers: ["Attribute", "Agencies", "ChatGPT", "DIY Research", "PersonaFi ⭐"],
        rows: [
          ["Accuracy", "High", "Variable", "Variable", "≥95% (verified)"],
          ["Cost", "€3K-€10K", "€20/mo", "\"Free\" (time)", "€1 per validation"],
          ["Time", "1-2 weeks", "10-30 min", "Days-weeks", "2 minutes"],
          ["Consistency", "High", "Low", "Low", "High (≥95%)"],
          ["Skill needed", "None", "High", "High", "None"],
          ["Payment", "Retainer", "Subscription", "Time cost", "Pay-per-use"],
          ["Iterations", "Limited", "Unlimited", "Unlimited", "Unlimited (€1)"],
        ],
        footer: "PersonaFi is the only solution combining professional accuracy + instant speed + radical affordability + zero barriers.",
      },
      agencyDiff: {
        title: "How To Position Without Losing Competitive Edge",
        subtitle: "\"If every agency can white-label this, how do I differentiate?\"",
        answer: "The answer: PersonaFi is infrastructure, not strategy.",
        diffTitle: "Your Differentiation Comes From:",
        diffs: [
          { bold: "Industry expertise", text: "You understand your client's competitive landscape" },
          { bold: "Strategic interpretation", text: "You translate validation into executable campaigns" },
          { bold: "Client relationships", text: "You have context PersonaFi will never have" },
          { bold: "Implementation", text: "You execute strategy-PersonaFi only validates" },
          { bold: "Custom frameworks", text: "Layer your methodology on top of PersonaFi" },
        ],
        guaranteeTitle: "Quality Guarantee:",
        guaranteeText: "If a client is dissatisfied with analysis quality, we'll revise at no cost or refund. Your reputation is our priority.",
        cta: "Book Agency Demo",
      },
      pricing: {
        title: "Simple, Honest Pricing",
        payPerUse: "Pay-Per-Use",
        price: "€1",
        perValidation: "per validation",
        noSubs: "No subscriptions. No commitments. No hidden fees.",
        items: ["First 3 validations: FREE", "95%+ accuracy guaranteed", "Full refund if not satisfied", "No credit card to start"],
        cta: "Start Free Validation Now",
        enterpriseTitle: "Enterprise Volume Pricing:",
        enterpriseDesc: "500-1000 validations/year: €0.60-€0.80 per validation • Custom API • Dedicated support",
        comparePersonaFi: "PersonaFi",
        comparePersonaFiPrice: "€10",
        comparePersonaFiDesc: "10 validations",
        compareAgency: "Agency",
        compareAgencyPrice: "€5K-€10K",
        compareAgencyDesc: "1 engagement",
        compareChatGPT: "ChatGPT Plus",
        compareChatGPTPrice: "€20/mo",
        compareChatGPTDesc: "unlimited (variable)",
      },
      faq: {
        title: "Frequently Asked Questions",
        items: [
          { q: "Is PersonaFi proven to work?", a: "Yes. Roman Ledak used it on his own campaigns: +158% reply rate, +100% website visits, +30% conversion. PersonaFi is newly launched to external users-your first 3 validations are FREE to test on your real marketing." },
          { q: "What does \"at least 95% accuracy\" mean?", a: "PersonaFi's reports match or exceed professional strategist assessments at least 95% of the time across 6 criteria. \"At least\" because PersonaFi often identifies gaps even experienced marketers miss." },
          { q: "I already use AI tools. Why do I need PersonaFi?", a: "Even AGAPE's founder found gaps he missed. Generic AI helps you create content. PersonaFi provides comprehensive validation across 6 criteria-feedback from angles you might not have considered. AI helps you build → PersonaFi helps you validate." },
          { q: "What if I'm skeptical about these results?", a: "Healthy skepticism is good! Roman's results are real and verifiable. Starting 3 validations are FREE. Full refund on paid validations. Download sample reports to judge quality first." },
          { q: "How is this different from ChatGPT?", a: "ChatGPT is general-purpose. PersonaFi is purpose-built: plain language (no prompting), consistent 95%+ accuracy, structured reports, marketing frameworks, 2 minutes guaranteed." },
          { q: "What content types can I validate?", a: "All marketing content: value propositions, sales scripts, email campaigns, landing pages, social media (Instagram, LinkedIn, TikTok), ICP definitions, product positioning, blog topics, ad messaging." },
          { q: "Is my data safe?", a: "Yes. Content analyzed but never stored permanently. Zero data retention. GDPR compliant. No data used for training, nor for marketing or sales purposes." },
          { q: "Can I use this for my agency clients?", a: "Absolutely. White-label as proprietary methodology. Bill clients €500 for €1 analysis. Deliver faster than competitors. We offer white-label options and agency demos." },
        ],
      },
      finalCta: {
        title: "Try It Free Right Now",
        subtitle: "For solopreneurs, content marketers, social media managers, agencies & enterprise teams",
        description: "Before you spend €500-€2,000 on your next campaign, invest 2 minutes to see if your personas and messaging make sense.",
        bullets: ["6 scores showing strong vs. weak areas", "Specific gaps in your persona/content", "Copy-paste recommendations for today", "Confidence to launch or iterate"],
        timeNote: "2 minutes • Starting 3 validations FREE",
        cta: "Start Free Validation Now",
        trustAccuracy: "≥95% accuracy",
        trustFree: "First 3 validations FREE",
        trustRefund: "Full refund guarantee",
        trustData: "We don't use your data",
        tagline: "AGAPE PersonaFi is part of AGAPE's value-driven AI consulting ecosystem.",
      },
    },
    blog: {
      heroTitle: "AGAPE Blog",
      heroSubtitle: "Practical playbooks on AI, sales and operations for SMEs that scale.",
      allCategories: "All",
      read: "Read",
      minutes: "min read",
      empty: "No articles yet — check back soon.",
      back: "Back to Blog",
      published: "Published",
      author: "Author",
      related: "You might also like",
      fallbackNotice: "Available in English",
      updatedOn: "Updated",
      readInEnglish: "Read in English",
      externalBadge: "LinkedIn",
      searchPlaceholder: "Search articles...",
      searchEmpty: "No articles match your search.",
      externalTooltip: "Opens on LinkedIn in a new tab",
      notFoundTitle: "Article not found",
      notFoundSubtitle: "This article doesn't exist or hasn't been published yet.",
      newsletter: {
        title: "Stay in the loop",
        description: "Get our new articles and field-tested frameworks straight to your inbox. No spam, no fluff.",
        placeholder: "your@email.com",
        subscribe: "Subscribe",
        success: "Thanks! You're on the list.",
        invalidEmail: "Please enter a valid email address.",
        error: "Something went wrong. Please try again.",
      },
      stickyCta: {
        ariaLabel: "Stay in touch",
        title: "Liked this read?",
        description: "Get the next one in your inbox.",
        bookCall: "Book a 25-min call",
        bookCallShort: "Book a call",
        dismiss: "Dismiss",
      },
      faqTitle: "Frequently asked questions",
      libertyNotePre: "This article was created with AGAPE's proprietary content machine ",
      libertyNoteLink: "AGAPE Liberty",
      libertyNotePost: ".",
    },
    personaFiPopup: {
      badge: "Free",
      title: "Get feedback that makes your content sell more",
      description:
        "With AGAPE PersonaFi you get free feedback on your content, so you can sharpen it and convert more readers into buyers - no sign-up, no card.",
      cta: "See PersonaFi",
      later: "Maybe later",
      dismiss: "Dismiss",
    },
    footer: {
      rights: "All rights reserved.",
      lastUpdated: "Site last updated",
      privacyPolicy: "Privacy Policy",
      cookiePolicy: "Cookie Policy",
    },
  },
  pl: {
    nav: {
      services: "Usługi",
      whyAgape: "Dlaczego AGAPE?",
      howWeWork: "Jak Pracujemy",
      caseStudies: "Case Studies",
      resources: "Zasoby",
      blog: "Blog",
      contact: "Kontakt",
      aboutUs: "O Nas",
      bookCall: "Umów Rozmowę",
    },
    hero: {
      tagline: "Inżynierowie Wzrostu B2B",
      headline: "Zamień chaos sprzedaży w przewidywalny silnik spotkań B2B",
      description: "AGAPE Automation Systems buduje bezpieczny, zautomatyzowany silnik outreachu, który dostarcza przewidywalne spotkania B2B — bez rekrutacji handlowców i bez palenia Twojej domeny.",
      bookCall: "Umów 25-min rozmowę",
      ourServices: "Zobacz Produkty",
    },
    services: {
      title: "Co Robimy",
      subtitle: "Kompleksowe usługi konsultingowe zaprojektowane, aby przyspieszyć rozwój Twojego biznesu i transformację cyfrową.",
      learnMore: "Zobacz szczegóły usługi",
      items: [
        {
          title: "Biznes Rozwój",
          description: "Napędzanie wzrostu poprzez strategiczną ekspansję rynkową",
          details: ["Analiza rynku i mapowanie możliwości", "Strategia wejścia na rynek", "Optymalizacja procesu sprzedaży", "Inicjatywy wzrostu przychodów"],
        },
        {
          title: "Marketing Cyfrowy",
          description: "Maksymalizacja Twojej obecności cyfrowej i zasięgu",
          details: ["Zarządzanie kampaniami wielokanałowymi", "Strategia i tworzenie treści", "Analityka i optymalizacja wydajności", "Pozycjonowanie marki i komunikacja"],
        },
        {
          title: "Interim Management",
          description: "Eksperckie przywództwo, gdy najbardziej go potrzebujesz",
          details: ["Role C-suite i wyższego kierownictwa", "Przywództwo transformacyjne", "Zarządzanie kryzysowe", "Transfer wiedzy i rozwój zespołu"],
        },
        {
          title: "Rozwiązania AI",
          description: "Wykorzystanie AI do transformacji operacji biznesowych",
          details: ["Gotowe rozwiązania", "Rozwiązania szyte na miarę", "Integracja rozwiązań i systemów"],
          hasButton: true,
        },
      ],
    },
    whyAgape: {
      title: "Dlaczego AGAPE?",
      subtitle: "Większość agencji sprzedaje działania i zostawia Cię z narzędziem. AGAPE pracuje na wynik sprzedażowy i dostarcza go w formie pełnej usługi white-glove.",
      values: [
        {
          title: "Sprzedaż, nie pseudo-wyniki",
          description: "W przeciwieństwie do agencji raportujących wyświetlenia, kliknięcia i wysłane wiadomości, AGAPE pracuje na to, co dla Ciebie najważniejsze — najczęściej liczbę sprzedaży. Żadnej roboty dla roboty ani fałszywych statystyk.",
        },
        {
          title: "Usługa white-glove, nie samo narzędzie",
          description: "W przeciwieństwie do narzędzi, z którymi zostajesz sam, AGAPE obsługuje dla Ciebie własny, topowy system pozyskiwania leadów i dostarcza rezultat: kwalifikowane leady i spotkania, a nie kolejne konto do obsługi.",
        },
        {
          title: "Szybkość i Elastyczność",
          description: "W przeciwieństwie do rekrutacji, która trwa miesiącami, typowy silnik działań sprzedażowych uruchamiamy w 14–28 dni: od przygotowania bazy i komunikacji po bezpieczną konfigurację domeny.",
        },
        {
          title: "Mierzalny Wpływ",
          description: "Mierzymy konkretne wyniki: KubaDrukuje osiągnęło 12 zamówień z Instagrama w trzy miesiące.",
        },
        {
          title: "Strategiczny Fokus",
          description: "Dla AleksanovInvest doprecyzowaliśmy personę i ofertę przed optymalizacją sprzedaży na LinkedIn; efektem był pierwszy płatny klient.",
        },
        {
          title: "Globalny Zasięg",
          description: "Przykład współpracy ponad granicami: platforma cyfrowa i cykliczne warsztaty AI dla białoruskiej diaspory w Krakowie.",
        },
      ],
    },
    howWeWork: {
      title: "Jak Pracujemy",
      subtitle: "Nasza sprawdzona 6-etapowa metodologia zapewnia spójne rezultaty i przejrzystą współpracę na każdym etapie.",
      cta: "Umów bezpłatną 25-minutową rozmowę",
      ctaHeading: "Gotowy rozpocząć współpracę z AGAPE?",
      steps: [
        {
          title: "Odkrycie",
          shortText: "Odkrycie to 25-minutowa rozmowa wstępna, podczas której oceniamy cele, wyzwania i obecną sytuację Twojej firmy, aby ustalić, czy współpraca z AGAPE to dobry wybór.",
          expandedText: "",
        },
        {
          title: "Głęboka Analiza",
          shortText: "Kompleksowa sesja analityczna",
          expandedText: "Dokładna 60-90 minutowa sesja, podczas której zagłębiamy się w Twoje konkretne potrzeby, analizujemy pozycję rynkową i identyfikujemy kluczowe możliwości wzrostu i usprawnień.",
        },
        {
          title: "Strategia",
          shortText: "Tworzenie spersonalizowanej roadmapy",
          expandedText: "Na podstawie naszej analizy opracowujemy dopasowaną strategię z jasnymi kamieniami milowymi, KPI i praktycznymi rekomendacjami zaprojektowanymi specjalnie dla Twojego kontekstu biznesowego.",
        },
        {
          title: "Propozycja",
          shortText: "Przejrzyste warunki współpracy",
          expandedText: "Otrzymujesz szczegółową propozycję określającą zakres, harmonogram, rezultaty i inwestycję. Wierzymy w pełną przejrzystość - bez ukrytych kosztów czy niespodzianek.",
        },
        {
          title: "Realizacja",
          shortText: "Praktyczna implementacja",
          expandedText: "Nasz zespół pracuje ramię w ramię z Twoim, aby wdrożyć strategię. Dostarczamy regularne aktualizacje, dostosowujemy się do zmieniających się okoliczności i zapewniamy stały postęp w realizacji celów.",
        },
        {
          title: "Rezultaty",
          shortText: "Mierzalne wyniki i wzrost",
          expandedText: "Mierzymy sukces poprzez konkretne rezultaty: wzrost przychodów, ekspansję rynkową, efektywność operacyjną lub inne wskaźniki najważniejsze dla Twojego biznesu.",
        },
      ],
    },
    caseStudies: {
      title: "Case Studies",
      subtitle: "Prawdziwe rezultaty z prawdziwych partnerstw. Odkryj, jak pomogliśmy firmom przekształcić wyzwania w możliwości.",
      asOf: "Wszystkie dane zweryfikowane na wrzesień 2026.",
      challenge: "Wyzwanie",
      solution: "Rozwiązanie",
      keyValue: "Kluczowa Wartość",
      learnMore: "Dowiedz się więcej",
      cases: [
        {
          company: "WorkForce PRO",
          challenge: "WorkForce PRO potrzebowało profesjonalnej obecności online, która zbuduje wizerunek eksperta w outsourcingu procesów i przyciągnie klientów B2B.",
          solution: "AGAPE opracowało strategię komunikacji, zarządza kontem LinkedIn firmy i tworzy newsletter, łącząc działania organiczne z kampaniami e-mail i LinkedIn Outreach.",
          keyValue: "Dzięki empatycznemu podejściu i konsekwentnej komunikacji AGAPE zbudowało markę od podstaw, z ponad 400 obserwującymi, 40 postami i zauważalnym wzrostem zainteresowania ofertą WorkForce PRO.",
        },
        {
          company: "VisualCore",
          challenge: "Visual Core potrzebowało szybkiego uruchomienia MVP systemu opartego na architekturze kognitywnej, zintegrowanego z komunikatorem Telegram i gotowego do monetyzacji.",
          solution: "AGAPE zbudowało chatbota połączonego z wektorową bazą danych (RAG) oraz system subskrypcji w dwa tygodnie. Każda sesja użytkownika była izolowana, zapewniając pełne bezpieczeństwo i skalowalność.",
          keyValue: "Współpraca zaowocowała szybkim startem sprzedaży i spójnym wizerunkiem marki na LinkedIn. AGAPE wsparło Visual Core w opracowaniu strategii marketingowej, planu treści i edukacyjnej komunikacji produktu.",
        },
        {
          company: "KubaDrukuje",
          challenge: "Kuba miał talent i pasję do druku 3D, ale brakowało mu kierunku, strategii i spójnej komunikacji marki. Pomysłów było wiele, ale brakowało struktury i skutecznego planu.",
          solution: "AGAPE pomogło zdefiniować misję marki, strategię i styl komunikacji. Zbudowaliśmy plan treści, uporządkowaliśmy ofertę i przejęliśmy działania marketingowe na Instagramie.",
          keyValue: "Profil KubaDrukuje na Instagramie urósł o 313% w ciągu trzech miesięcy, osiągając 27 000 wyświetleń i 12 zamówień dzięki uporządkowanej strategii marketingowej i contentowej AGAPE.",
        },
        {
          company: "Himtek",
          challenge: "HIMTEK, firma przemysłowa, potrzebowała zwiększyć rozpoznawalność marki i generować więcej zapytań przez LinkedIn - kanał, którego potencjał nie był dotąd w pełni wykorzystany.",
          solution: "AGAPE uruchomiło konto firmowe, publikując dwa posty tygodniowo i budując komunikację skierowaną do potencjalnych klientów B2B. Skupiliśmy się na treściach sprzedażowych i wizerunkowych.",
          keyValue: "Już po pierwszym miesiącu opracowano wytyczne optymalizacji treści pod konwersję oraz strategię dalszego rozwoju. HIMTEK zyskał aktywny kanał komunikacji i solidną bazę do przyszłych kampanii.",
        },
        {
          company: "DanGuitarra",
          challenge: "Nauczyciel gitary potrzebował zwiększyć widoczność i zainteresowanie lekcjami wśród lokalnych uczniów, szczególnie wśród młodszych grup wiekowych aktywnych w mediach społecznościowych.",
          solution: "AGAPE opracowało strategię marketingową opartą na TikToku, przygotowało plan treści, scenariusze i nagrania z klientem oraz zajęło się montażem wideo i copywritingiem postów.",
          keyValue: "Stworzono kompletny system marketingowy i operacyjny: treści, kampanię TikTok, rezerwacje online z płatnościami i powiadomieniami SMS. Szkoła zyskała strukturę, automatyzację i widoczność online.",
        },
        {
          company: "Polesie United",
          challenge: "Polesie United zaczynało jako zespół znajomych bez strategii, struktury i rozpoznawalności. Brakowało spójnej komunikacji, zasobów i wsparcia finansowego do rozwoju w profesjonalny projekt.",
          solution: "AGAPE przejęło kompleksowy marketing klubu - od rebrandingu i mediów społecznościowych po sklep z gadżetami i strategię treści. Zapewniliśmy też wsparcie organizacyjne przy przekształceniu w stowarzyszenie i przygotowaliśmy ofertę sponsorską.",
          keyValue: "W zaledwie rok Polesie United przeszło od drużyny znajomych do pełnoprawnego klubu z ponad 1000 obserwujących, aktywną społecznością i pierwszym sponsorem, który uwierzył w projekt. AGAPE zbudowało markę, strukturę i przyszłość klubu.",
        },
        {
          company: "American Food Production Company",
          challenge: "Klient miał ponad 2000 plików w 20-letnim archiwum Google Drive. Brak struktury, organizacji i szybkiego dostępu do danych utrudniał pracę operacyjną i zarządzanie dokumentami.",
          solution: "AGAPE w pełni zdigitalizowało zasoby, tworząc Company Operating System w Notion. Dokumenty zostały posegmentowane, opisane i połączone z wyszukiwaniem treści przy użyciu Notion AI.",
          keyValue: "Efektem było centralne repozytorium wiedzy, które przekształciło chaos w przejrzysty ekosystem. Klient zyskał natychmiastowy dostęp do 20 lat historii firmy i solidną podstawę do dalszej automatyzacji.",
        },
        {
          company: "AleksanovInvest",
          challenge: "AleksanovInvest – marka edukacyjna w obszarze inwestycji – nie miała jasno zdefiniowanego brandu, persony klienta ani spójnej oferty. Początkowa komunikacja była wycelowana w niewłaściwy segment, a kanał komunikacji marketingowej został dobrany niewłaściwie. Sam sposób komunikacji był mało efektywny – przynosił niewielkie efekty sprzedażowe przy wielkich nakładach wysiłku. Właściciel nie miał też pewności, jak ma wyglądać produkt i komu go realnie sprzedawać.",
          solution: "W ciągu pierwszych paru miesięcy współpracy AGAPE poprowadziło klienta przez ustrukturyzowany proces strategiczny w cotygodniowym rytmie: opisanie brandu i persony, wielokrotne iterowanie oferty, dopasowanie komunikacji do nowego ICP oraz uruchomienie i optymalizacja kampanii outreach na LinkedIn. Równolegle uporządkowano stronę www, ofertę konsultacji i fundamenty sprzedażowe.",
          keyValue: "Współpraca dała Romanowi Aleksanowowi zdecydowaną pewność co do tego, jak ma wyglądać jego produkt, co robić, żeby się sprzedawał, i do kogo trafiać. Kampania LinkedIn rozbudowała network, pozwoliła wyłapać skuteczne wiadomości outboundowe i przyniosła konwersję na pierwszego płatnego deala – na bazie nowej oferty i spójnych fundamentów marki. Obecnie AGAPE wspiera AleksanovInvest w zakresie pozyskiwania klientów.",
        },
        {
          company: "Belarusian Diaspora in Krakow",
          challenge: "Społeczność diaspory potrzebowała nie tylko profesjonalnej infrastruktury cyfrowej od zera, ale przede wszystkim wyjścia z wykluczenia technologicznego. Brakowało centralnego punktu komunikacji oraz praktycznej wiedzy, jak członkowie mogą wykorzystać sztuczną inteligencję do podnoszenia osobistej i biznesowej produktywności.",
          solution: "AGAPE zaprojektowało i wdrożyło kompletną architekturę webową (belkrakow.org) – od konfiguracji domeny po UI/UX. Równolegle Roman Ledak uruchomił „Labaratoryja Budućyni” (The Future Lab) – cykliczny program warsztatowy, uczący wdrażania narzędzi AI w codziennych operacjach i zadaniach.",
          keyValue: "Zbudowano stabilny i bezobsługowy ekosystem cyfrowy dla społeczności. Każdego miesiąca członkowie społeczności zyskują twarde kompetencje podczas warsztatów, bezpośrednio przekładając narzędzia AI na automatyzację i optymalizację własnego biznesu, budując głębokie zaufanie do kompetencji AGAPE.",
        },
        {
          company: "Natalia Pietrów",
          challenge: "Monetyzacja wysoce specjalistycznej wiedzy (biohacking) wymagała zbudowania od podstaw skalowalnej infrastruktury sprzedażowej i ustrukturyzowanej strategii marketingowej, bez generowania chaosu i zwiększania kosztów operacyjnych twórcy.",
          solution: "AGAPE wdrożyło kompletną architekturę operacyjną: od zdefiniowania Brand Core i USP, przez zaprojektowanie struktury produktu, aż po techniczną infrastrukturę e-commerce (landing page, domena, automatyzacja dystrybucji e-booka i powiązanie wideo kodami QR).",
          keyValue: "Twórca zyskał zautomatyzowany system docierania do klientów i mógł w 100% skupić się na merytoryce. Eliminacja pracy manualnej zabezpieczyła stabilny, przewidywalny proces sprzedaży i stały napływ leadów dla nowej marki.",
        },
        {
          company: "Spotless Polska",
          challenge: "Marcin (Wrocław) wchodził na rynek z usługą mycia paneli fotowoltaicznych i przeszkleń bez chemii. Miał technologię, ale nie miał marki, strony, cennika ani referencji – potrzebował sprawdzenia popytu, a nie ładnej prezentacji firmy.",
          solution: "AGAPE zbudowało markę od zera (domena, skrzynka z budowaniem reputacji, strona internetowa, projekty logo i ulotki), a następnie przygotowało bazę ok. 1000 firm i poprowadziło kampanie e-mail do czterech segmentów: farm fotowoltaicznych, salonów samochodowych, hal i zakładów produkcyjnych.",
          keyValue: "743 maile dotarły do 385 firm i przyniosły 38 odpowiedzi (9,9%) oraz 4 zapytań ofertowych. U klienta zostają cztery gotowe kampanie i baza – działający fundament sprzedaży, a nie jednorazowa akcja.",
        },
      ],
    },
    resources: {
      title: "Zasoby i Inspiracje",
      subtitle: "Bądź na bieżąco z naszymi najnowszymi przemyśleniami na temat AI, transformacji cyfrowej i strategii biznesowej.",
      readOn: "Czytaj na LinkedIn",
      viewAll: "Zobacz Wszystkie na LinkedIn",
      posts: [
        {
          title: "Automatyzacja AI i Transformacja Cyfrowa",
          excerpt: "Jak automatyzacja napędzana przez AI przyspiesza transformację cyfrową w różnych branżach i pomaga firmom rozwijać się mądrzej.",
        },
        {
          title: "Adopcja AI w Polskich Firmach",
          excerpt: "Jak polskie firmy wykorzystują sztuczną inteligencję do zwiększenia innowacyjności i efektywności.",
        },
        {
          title: "Partnerstwo Człowiek-AI",
          excerpt: "Jak ludzka kreatywność i synergia z AI redefiniują produktywność i współpracę.",
        },
        {
          title: "Generatywna AI i Kreatywność",
          excerpt: "Rozwój generatywnej AI w projektowaniu i innowacjach treściowych.",
        },
        {
          title: "Humor AI i Lekcje z Życia",
          excerpt: "Lekkie spojrzenie na błędy AI, które odkrywają głębsze prawdy o innowacjach.",
        },
        {
          title: "Szkolenie z Higieny Cyfrowej AGAPE",
          excerpt: "Praktyczna seria warsztatów o cyfrowym dobrostanie i mądrzejszym korzystaniu z technologii.",
        },
      ],
    },
    contact: {
      title: "Rozpocznijmy Rozmowę",
      subtitle: "Gotowy odkryć, jak AGAPE może pomóc w transformacji Twojego biznesu? Umów bezpłatną 25-minutową rozmowę i porozmawiajmy o Twoich celach.",
      bookCallTitle: "Umów Bezpłatną Rozmowę",
      bookCallDesc: "Zaplanuj 25-minutową rozmowę, aby omówić potrzeby Twojego biznesu i zbadać, jak możemy pomóc.",
      bookCallBtn: "Umów przez Calendly",
      contactTitle: "Informacje Kontaktowe",
      followUs: "Obserwuj Nas",
    },
    aboutUs: {
      heroTitle: "Uwalniamy Cyfrowy Potencjał",
      heroSubtitle: "Pomagamy firmom odnaleźć spokój, pewność siebie i realną wartość poprzez empatię i mistrzostwo technologiczne.",
      corePhilosophy: "Dostępne Wzmocnienie",
      corePhilosophyDesc1: "Przełamujemy bariery kosztowe i technologiczne, aby innowacja była",
      corePhilosophyDesc2: "prosta i osiągalna.",
      founderJourney: {
        title: "Droga Założyciela",
        subtitle: "Od IBM do AGAPE",
        corporate: {
          period: "2020-2023",
          title: "Fundamenty Korporacyjne",
          company: "IBM Consulting",
          role: "Konsultant Automatyzacji",
          description: "Roman Ledak pracował jako Konsultant Automatyzacji w IBM Consulting. Prowadził dziesiątki projektów end-to-end i ocenił setki procesów dla globalnych liderów finansów i logistyki."
        },
        commercial: {
          period: "2023-2025",
          title: "Most Komercyjny",
          company: "Marketing Automation Agency",
          role: "Dyrektor Dostarczania",
          description: "Jako Dyrektor Dostarczania w agencji Marketing Automation, Roman przesunął fokus z oszczędności operacyjnych na generowanie przychodów, pionierując marketing napędzany AI, który zwiększył produktywność zespołu o 40%."
        },
        breakthrough: {
          period: "2025",
          title: "Przełom AGAPE",
          description: "Założona, aby pokonać lukę w \"Gospodarce Post-Zatrudnieniowej\", umożliwiając MŚP skalowanie operacji i sprzedaży bez zwiększania zatrudnienia."
        },
        evolution: {
          period: "2026",
          title: "Ewolucja do AGAPE AUTOMATION SYSTEMS",
          description: "Połączenie zapału dwóch młodych inżynierów z uzupełniającymi się kompetencjami i wspólnymi wartościami umożliwiające skalowanie wartości."
        }
      },
      specializedEdge: {
        title: "Nasza Przewaga",
        subtitle: "Rozpoznawanie Wzorców",
        diagnostic: {
          title: "Talent Diagnostyczny",
          description: "AGAPE doskonale diagnozuje, gdzie dokładnie traci się wartość w złożonych operacjach."
        },
        stability: {
          title: "Stabilność Klasy Przemysłowej",
          description: "W przeciwieństwie do \"napędzanego hype'em\" rynku AI, dostarczamy stabilną, niezawodną automatyzację opartą na standardach korporacyjnych."
        },
        goal: {
          title: "Cel",
          description: "Projektować technologię, która znika w tle, pozwalając Ci skupić się na Twojej wizji."
        }
      },
      values: {
        title: "Wartości AGAPE",
        subtitle: "\"Najwyższa Forma Miłości\"",
        intro: "Redefiniujemy konsulting przez pryzmat empatii i mierzalnej wartości.",
        items: [
          {
            title: "Napędzani Wartością",
            impact: "Każde działanie musi dostarczać mierzalną, policzalną wartość dla Twojego biznesu."
          },
          {
            title: "Mędrzec-Innowator",
            impact: "Łączymy mądrość przewodnika z energią twórcy, aby rozwiązywać problemy."
          },
          {
            title: "Empatia i AGAPE",
            impact: "Rozumiemy, że problemy biznesowe powodują osobisty stres; dążymy do zapewnienia Ci spokoju ducha."
          },
          {
            title: "Partnerstwo",
            impact: "Nie tylko dostarczamy; budujemy wewnętrzne kompetencje i pewność Twojego zespołu."
          }
        ]
      },
      credibility: {
        title: "Dlaczego Nam Zaufać?",
        subtitle: "Filary Wiarygodności",
        pillars: [
          {
            title: "Udowodnione Wyniki",
            description: "Miliony zaoszczędzone dla globalnych liderów i setki zoptymalizowanych procesów."
          },
          {
            title: "Strategia Agencji, Cena MŚP",
            description: "Strategia na poziomie enterprise w cenie dostępnej dla ambitnych firm."
          },
          {
            title: "Młodzi i Otwarci",
            description: "Zespół napędzany myśleniem \"poza schematami\" i kreatywnym rozwiązywaniem problemów."
          }
        ]
      },
      cta: {
        title: "Gotowy na przełom?",
        subtitle: "Między tym, gdzie jesteś, a tym, gdzie chcesz być?",
        button: "Umów Bezpłatną 25-Minutową Rozmowę"
      }
    },
    personaFi: {
      hero: {
        tagline: "AGAPE • AI Oparte na Wartości dla Marketerów",
        title: "Sprawdź swój marketing w",
        titleHighlight1: "2 minuty",
        titleConnector: "za",
        titleHighlight2: "€1",
        description: "Przestań zgadywać, czy Twoje persony i komunikacja zadziałają. Uzyskaj walidację opartą na frameworkach w 2 minuty - bez retainerów agencyjnych za €5 000, tygodni oczekiwania czy ekspertyzy w promptowaniu AI.",
        founderLabel: "Przetestowane przez Założyciela, Udowodnione Wyniki:",
        founderQuote: "\"Mimo że mam solidną ekspertyzę w marketingu i byłem wspierany przez AI wcześniej - wciąż brakowało mi perspektyw. PersonaFi dostarczyło 360-stopniowe dane zwrotne.\"",
        founderName: "- Roman Ledak, Założyciel AGAPE, Kraków",
        statReply: "+158% wskaźnik odpowiedzi",
        statVisits: "+100% wizyt na stronie",
        statConversion: "+30% konwersja",
        ctaTry: "Wypróbuj Za Darmo (30 sek)",
        ctaSample: "Zobacz Przykładowy Raport",
        trustAccuracy: "Co najmniej 95% dokładności",
        trustFree: "3 uruchomienia GRATIS na start",
        trustRefund: "Pełna gwarancja zwrotu",
        trustData: "Nie wykorzystujemy Twoich danych",
        versionNote: "PersonaFi v1.0 · stan na wrzesień 2026",
      },
      stats: {
        title: "W Liczbach",
        founderTested: "PRZETESTOWANE",
        founderTestedDesc: "Prawdziwa walidacja, prawdziwe wyniki",
        replyRate: "+158%",
        replyRateDesc: "Poprawa wskaźnika odpowiedzi (12%→31%)",
        accuracy: "≥95%",
        accuracyDesc: "Dokładność zweryfikowana przez profesjonalistów",
        price: "€1",
        priceDesc: "Za walidację - taniej niż kawa",
        tagline: "Prawdziwa walidacja. Prawdziwy wpływ. Prawdziwi marketerzy.",
        asOf: "Dane na wrzesień 2026.",
      },
      beforeAfter: {
        title: "Zobacz PersonaFi w Akcji",
        subtitle: "Prawdziwe przykłady pokazujące, jak PersonaFi transformuje wyniki marketingowe.",
        example1Title: "Prawdziwy Przykład: Walidacja Kampanii Outreach",
        example1Subtitle: "Roman Ledak (Założyciel AGAPE) miał 12% wskaźnik odpowiedzi. Oto co ujawnił PersonaFi:",
        beforeTitle: "Przed PersonaFi",
        beforeText: "\"Cześć [Imię], pomagam firmom ulepszać marketing z AI. Czy byłbyś zainteresowany, jak możemy Ci pomóc?\"",
        beforeResult: "Wynik: 12% wskaźnik odpowiedzi",
        identifiedTitle: "Co zidentyfikował PersonaFi",
        identifiedScore: "Ocena Dopasowania Wiadomość-Persona:",
        identifiedItems: ["Brak konkretnego problemu klienta", "\"Ulepszanie marketingu z AI\" zbyt ogólne", "Brak wyzwalacza zakupowego", "CTA zakłada zainteresowanie"],
        afterTitle: "Po Rewizji PersonaFi",
        afterText: "\"Cześć [Imię], widziałem, że uruchamiasz [nową usługę]. Większość założycieli marnuje €500-€2K testując komunikację, która nie rezonuje...\"",
        afterReply: "31% wskaźnik odpowiedzi (+158%)",
        afterVisits: "+100% wizyt na stronie",
        afterConversion: "+30% konwersja",
        realExample: "TO JEST PRAWDZIWY PRZYKŁAD od założyciela PersonaFi.",
        example2Title: "Walidacja Treści Social Media",
        example2Subtitle: "Co wpisał manager social media:",
        example2Input: "Persona: \"Użytkownicy Instagrama 25-35 zainteresowani zrównoważoną modą\" | Treść: Karuzela Instagram \"10 Sposobów na Garderobę Kapsułową\"",
        example2Score: "Ocena Dopasowania Treść-Persona:",
        example2ScoreLabel: "3/5 (Umiarkowane)",
        example2WhyTitle: "Dlaczego to nie trafia:",
        example2WhyItems: ["\"Garderoba kapsułowa\" ≠ zrównoważony rozwój", "Brak zrównoważonych marek lub materiałów", "Brak perspektywy wpływu na środowisko", "\"10 sposobów\" edukuje, ale nie angażuje"],
        example2StrongerTitle: "Lepsze podejścia do treści:",
        example2StrongerItems: ["\"10 Zrównoważonych Marek do Twojej Garderoby Kapsułowej\"", "\"Jak Zbudowałam Garderobę Kapsułową Bez Fast Fashion\"", "\"Wpływ na Środowisko: Garderoba 10 vs 100 Elementów\""],
        example2TipsTitle: "Wskazówki na Instagram:",
        example2TipsItems: ["Dodaj ankietę: \"Którą zrównoważoną markę następną?\"", "Kąt UGC: Poproś obserwujących o udostępnienie garderoby"],
        ctaTryFree: "Wypróbuj Darmową Walidację",
      },
      accuracy: {
        title: "Jak Mierzymy \"Co Najmniej 95% Dokładności\"",
        subtitle: "PersonaFi osiąga co najmniej 95% dokładności w porównaniu z doświadczonymi profesjonalistami marketingu.",
        criteriaIntro: "Porównujemy raporty PersonaFi z ocenami strategów (10+ lat doświadczenia) w 6 kryteriach:",
        criteria: ["Jasność i kompletność ICP", "Trafność problemów klienta", "Rezonans komunikacji", "Obsługa obiekcji", "Pozycjonowanie konkurencyjne", "Wyzwalacze psychologiczne"],
        whyTitle: "Dlaczego \"co najmniej\"?",
        whyText: "W wielu przypadkach PersonaFi identyfikuje luki, które nawet doświadczeni marketerzy przeoczają. Ustawiliśmy 95% jako minimum - faktyczne dopasowanie często je przekracza.",
        conclusion: "Analiza na poziomie profesjonalnym bez zatrudniania profesjonalistów.",
      },
      howItWorks: {
        title: "Jak Działa PersonaFi",
        subtitle: "4 proste kroki. 2 minuty łącznie. Zero umiejętności technicznych.",
        steps: [
          { title: "Wklej Swoją Treść", desc: "Udostępnij to, co chcesz zwalidować: propozycję wartości, e-mail, landing page, social media.", example: "Zwykły tekst wystarczy - bez specjalnego formatowania." },
          { title: "Opisz Swojego Klienta", desc: "Wpisz 2-3 zdania o tym, do kogo kierujesz komunikację.", example: "\"Właściciele małych firm, którzy mają problem z marketingiem i nie mają budżetu na agencje\"" },
          { title: "Otrzymaj Natychmiastową Walidację", desc: "Zwraca ustrukturyzowany raport z ocenami, zielonymi flagami, czerwonymi flagami i konkretnymi rekomendacjami.", example: "Raport z ocenami i konkretnymi rekomendacjami." },
          { title: "Iteruj lub Uruchom", desc: "Wykorzystaj feedback do iteracji i ulepszenia materiałów, lub użyj go w swoich prezentacjach.", example: "Ta cena pozwala przeprowadzić wiele iteracji, aby osiągnąć perfekcję w marketingu." },
        ],
        cta: "Rozpocznij Darmową Walidację",
      },
      whoItsFor: {
        title: "Stworzone Dla Marketerów, Którzy Nie Mogą Sobie Pozwolić na Zgadywanie",
        personas: [
          { title: "Soloprzedsiębiorcy i Małe Zespoły", benefits: ["Zwaliduj komunikację przed wydaniem na reklamy", "Uzyskaj bezstronną drugą opinię o swoim pitchu", "Uruchom z pewnością, nie na przeczuciu"] },
          { title: "Managerowie Treści i Marketingu", benefits: ["Przetestuj wiele kątów kampanii w jedno popołudnie", "Buduj decyzje oparte na danych dla kierownictwa", "Priorytetyzuj treści rezonujące z Twoim ICP"] },
          { title: "Marketerzy Social Media", benefits: ["Szybko waliduj koncepcje social media", "Porównaj wiele kątów treści obok siebie", "Optymalizuj hooki i CTA przed publikacją"] },
          { title: "Agencje Marketingowe", benefits: ["Fakturuj klientom €500 za analizę kosztującą Cię €1", "White-label raporty jako własną metodologię", "Skaluj spersonalizowane insighty dla wszystkich kont"] },
          { title: "Zespoły Enterprise (10+)", benefits: ["Zwaliduj 8+ segmentów w mniej niż 2 dni", "Integruj insighty z workflow CRM", "Wyrównaj zespoły cross-funkcyjne wokół zwalidowanych danych"] },
        ],
      },
      testimonial: {
        title: "Prawdziwe Wyniki Użytkowników PersonaFi",
        quote: "\"Po tym jak zacząłem używać PersonaFi do walidacji i ulepszania moich kampanii outreach, wyniki były oszałamiające. PersonaFi pokazał mi dokładnie, czego brakowało w mojej komunikacji i jak to naprawić. Mimo że mam solidną ekspertyzę w marketingu i byłem wspierany przez AI wcześniej - wciąż brakowało mi perspektyw. PersonaFi dostarczył 360-stopniowe dane zwrotne.\"",
        badgeReply: "+158% wskaźnik odpowiedzi",
        badgeVisits: "+100% wizyt na stronie",
        badgeConversion: "+30% konwersja",
        authorName: "Roman Ledak, Założyciel",
        authorCompany: "AGAPE • Kraków, Polska",
        invitation: "PersonaFi jest świeżo uruchomiony. Jeśli pomoże Ci ulepszyć marketing, chętnie zaprezentujemy Twoje wyniki.",
        invitationCta: "Wypróbuj Darmową Walidację - Dołącz do Naszych Historii Sukcesu",
      },
      comparison: {
        title: "Jak PersonaFi Wypada na Tle Alternatyw",
        headers: ["Atrybut", "Agencje", "ChatGPT", "Własny Research", "PersonaFi ⭐"],
        rows: [
          ["Dokładność", "Wysoka", "Zmienna", "Zmienna", "≥95% (zweryfikowana)"],
          ["Koszt", "€3K-€10K", "€20/mies.", "\"Darmowy\" (czas)", "€1 za walidację"],
          ["Czas", "1-2 tygodnie", "10-30 min", "Dni-tygodnie", "2 minuty"],
          ["Spójność", "Wysoka", "Niska", "Niska", "Wysoka (≥95%)"],
          ["Wymagane umiejętności", "Brak", "Wysokie", "Wysokie", "Brak"],
          ["Płatność", "Retainer", "Subskrypcja", "Koszt czasu", "Pay-per-use"],
          ["Iteracje", "Ograniczone", "Nielimitowane", "Nielimitowane", "Nielimitowane (€1)"],
        ],
        footer: "PersonaFi to jedyne rozwiązanie łączące profesjonalną dokładność + natychmiastową szybkość + radykalną przystępność cenową + zero barier.",
      },
      agencyDiff: {
        title: "Jak Się Wyróżnić Bez Utraty Przewagi Konkurencyjnej",
        subtitle: "\"Jeśli każda agencja może to white-labelować, jak się wyróżnię?\"",
        answer: "Odpowiedź: PersonaFi to infrastruktura, nie strategia.",
        diffTitle: "Twoje Wyróżnienie Pochodzi z:",
        diffs: [
          { bold: "Ekspertyza branżowa", text: "Rozumiesz krajobraz konkurencyjny swojego klienta" },
          { bold: "Interpretacja strategiczna", text: "Przekładasz walidację na wykonywalne kampanie" },
          { bold: "Relacje z klientami", text: "Masz kontekst, którego PersonaFi nigdy nie będzie mieć" },
          { bold: "Implementacja", text: "Realizujesz strategię - PersonaFi tylko waliduje" },
          { bold: "Własne frameworki", text: "Nałóż swoją metodologię na PersonaFi" },
        ],
        guaranteeTitle: "Gwarancja Jakości:",
        guaranteeText: "Jeśli klient jest niezadowolony z jakości analizy, poprawimy bez kosztów lub zwrócimy pieniądze. Twoja reputacja jest naszym priorytetem.",
        cta: "Umów Demo dla Agencji",
      },
      pricing: {
        title: "Prosta, Uczciwa Wycena",
        payPerUse: "Pay-Per-Use",
        price: "€1",
        perValidation: "za walidację",
        noSubs: "Bez subskrypcji. Bez zobowiązań. Bez ukrytych opłat.",
        items: ["Pierwsze 3 walidacje: GRATIS", "95%+ gwarancja dokładności", "Pełny zwrot przy niezadowoleniu", "Bez karty kredytowej na start"],
        cta: "Rozpocznij Darmową Walidację",
        enterpriseTitle: "Cennik Enterprise:",
        enterpriseDesc: "500-1000 walidacji/rok: €0.60-€0.80 za walidację • Własne API • Dedykowane wsparcie",
        comparePersonaFi: "PersonaFi",
        comparePersonaFiPrice: "€10",
        comparePersonaFiDesc: "10 walidacji",
        compareAgency: "Agencja",
        compareAgencyPrice: "€5K-€10K",
        compareAgencyDesc: "1 zlecenie",
        compareChatGPT: "ChatGPT Plus",
        compareChatGPTPrice: "€20/mies.",
        compareChatGPTDesc: "nielimitowane (zmienne)",
      },
      faq: {
        title: "Najczęściej Zadawane Pytania",
        items: [
          { q: "Czy PersonaFi naprawdę działa?", a: "Tak. Roman Ledak użył go w swoich kampaniach: +158% wskaźnik odpowiedzi, +100% wizyt na stronie, +30% konwersja. PersonaFi jest świeżo uruchomiony dla zewnętrznych użytkowników - Twoje pierwsze 3 walidacje są GRATIS, aby przetestować na swoim prawdziwym marketingu." },
          { q: "Co oznacza \"co najmniej 95% dokładności\"?", a: "Raporty PersonaFi dorównują lub przewyższają oceny profesjonalnych strategów co najmniej w 95% przypadków w 6 kryteriach. \"Co najmniej\", ponieważ PersonaFi często identyfikuje luki, które nawet doświadczeni marketerzy przeoczają." },
          { q: "Już korzystam z narzędzi AI. Dlaczego potrzebuję PersonaFi?", a: "Nawet założyciel AGAPE znalazł luki, które przeoczył. Generyczne AI pomaga tworzyć treści. PersonaFi zapewnia kompleksową walidację w 6 kryteriach - feedback z perspektyw, które możesz nie brać pod uwagę. AI pomaga budować → PersonaFi pomaga walidować." },
          { q: "Co jeśli jestem sceptyczny wobec tych wyników?", a: "Zdrowy sceptycyzm jest dobry! Wyniki Romana są prawdziwe i weryfikowalne. Pierwsze 3 walidacje są GRATIS. Pełny zwrot za płatne walidacje. Pobierz przykładowe raporty, aby ocenić jakość." },
          { q: "Czym to się różni od ChatGPT?", a: "ChatGPT jest ogólnego przeznaczenia. PersonaFi jest zbudowany celowo: prosty język (bez promptowania), spójna 95%+ dokładność, ustrukturyzowane raporty, frameworki marketingowe, gwarantowane 2 minuty." },
          { q: "Jakie typy treści mogę walidować?", a: "Wszystkie treści marketingowe: propozycje wartości, skrypty sprzedażowe, kampanie e-mail, landing page, social media (Instagram, LinkedIn, TikTok), definicje ICP, pozycjonowanie produktu, tematy blogów, komunikacja reklamowa." },
          { q: "Czy moje dane są bezpieczne?", a: "Tak. Treści analizowane, ale nigdy przechowywane na stałe. Zero retencji danych. Zgodność z GDPR. Dane nie są używane do treningu, ani do celów marketingowych czy sprzedażowych." },
          { q: "Czy mogę tego użyć dla klientów mojej agencji?", a: "Absolutnie. White-label jako własna metodologia. Fakturuj klientom €500 za analizę kosztującą €1. Dostarczaj szybciej niż konkurencja. Oferujemy opcje white-label i dema dla agencji." },
        ],
      },
      finalCta: {
        title: "Wypróbuj Teraz Za Darmo",
        subtitle: "Dla soloprzedsiębiorców, content marketerów, managerów social media, agencji i zespołów enterprise",
        description: "Zanim wydasz €500-€2 000 na następną kampanię, zainwestuj 2 minuty, aby sprawdzić, czy Twoje persony i komunikacja mają sens.",
        bullets: ["6 ocen pokazujących mocne vs. słabe obszary", "Konkretne luki w Twojej personie/treści", "Rekomendacje do skopiowania na dziś", "Pewność uruchomienia lub iteracji"],
        timeNote: "2 minuty • Pierwsze 3 walidacje GRATIS",
        cta: "Rozpocznij Darmową Walidację",
        trustAccuracy: "≥95% dokładności",
        trustFree: "Pierwsze 3 walidacje GRATIS",
        trustRefund: "Pełna gwarancja zwrotu",
        trustData: "Nie wykorzystujemy Twoich danych",
        tagline: "AGAPE PersonaFi jest częścią ekosystemu konsultingowego AGAPE opartego na wartości.",
      },
    },
    blog: {
      heroTitle: "Blog AGAPE",
      heroSubtitle: "Praktyczne playbooki z AI, sprzedaży i operacji dla MŚP, które skalują się bez powiększania zespołu.",
      allCategories: "Wszystkie",
      read: "Czytaj",
      minutes: "min czytania",
      empty: "Brak artykułów - wkrótce coś się pojawi.",
      back: "Powrót do bloga",
      published: "Opublikowano",
      author: "Autor",
      related: "Może Cię też zainteresować",
      fallbackNotice: "Dostępne po angielsku",
      updatedOn: "Aktualizacja",
      readInEnglish: "Czytaj po angielsku",
      externalBadge: "LinkedIn",
      searchPlaceholder: "Szukaj artykułów...",
      searchEmpty: "Żaden artykuł nie pasuje do wyszukiwania.",
      externalTooltip: "Otwiera się na LinkedIn w nowej karcie",
      notFoundTitle: "Nie znaleziono artykułu",
      notFoundSubtitle: "Ten artykuł nie istnieje lub nie został jeszcze opublikowany.",
      newsletter: {
        title: "Bądź na bieżąco",
        description: "Otrzymuj nasze nowe artykuły i sprawdzone w boju frameworki prosto na skrzynkę. Bez spamu, bez waty.",
        placeholder: "twoj@email.com",
        subscribe: "Zapisz się",
        success: "Dzięki! Jesteś na liście.",
        invalidEmail: "Podaj poprawny adres e-mail.",
        error: "Coś poszło nie tak. Spróbuj ponownie.",
      },
      stickyCta: {
        ariaLabel: "Zostańmy w kontakcie",
        title: "Podobał Ci się ten artykuł?",
        description: "Dostań kolejny prosto na skrzynkę.",
        bookCall: "Umów 25-min rozmowę",
        bookCallShort: "Umów rozmowę",
        dismiss: "Zamknij",
      },
      faqTitle: "Najczęściej zadawane pytania",
      libertyNotePre: "Artykuł powstał przy użyciu autorskiej kontent maszyny AGAPE — ",
      libertyNoteLink: "AGAPE Liberty",
      libertyNotePost: ".",
    },
    personaFiPopup: {
      badge: "Za darmo",
      title: "Otrzymaj informację zwrotną, która sprawi, że Twoja treść będzie sprzedawać więcej",
      description:
        "Z PersonaFi otrzymujesz bezpłatną informację zwrotną odnośnie swojej treści, dzięki czemu możesz ją ulepszyć i sprzedawać więcej — bez rejestracji, bez karty.",
      cta: "Zobacz PersonaFi",
      later: "Może później",
      dismiss: "Zamknij",
    },
    footer: {
      rights: "Wszelkie prawa zastrzeżone.",
      lastUpdated: "Ostatnia aktualizacja strony",
      privacyPolicy: "Polityka Prywatności",
      cookiePolicy: "Polityka Cookies",
    },
  },
  by: {
    nav: {
      services: "Паслугі",
      whyAgape: "Чаму AGAPE?",
      howWeWork: "Як Мы Працуем",
      caseStudies: "Кейсы",
      resources: "Рэсурсы",
      blog: "Блог",
      contact: "Кантакт",
      aboutUs: "Пра Нас",
      bookCall: "Запісацца на Спатканне",
    },
    hero: {
      tagline: "Інжынеры Росту B2B",
      headline: "Ператвары хаос у продажах у прадказальны рухавік сустрэч B2B",
      description: "AGAPE Automation Systems будуе бяспечны, аўтаматызаваны рухавік outreach, які дае прадказальныя B2B сустрэчы — без найму SDR і без спалення вашага дамена.",
      bookCall: "Запісацца на 25-хв размову",
      ourServices: "Глядзець Прадукты",
    },
    services: {
      title: "Што Мы Робім",
      subtitle: "Комплексныя кансалтынгавыя паслугі, створаныя для паскарэння росту вашага бізнесу і лічбавай трансфармацыі.",
      learnMore: "Глядзець падрабязнасці паслугі",
      items: [
        {
          title: "Бізнес Развіццё",
          description: "Стымуляванне росту праз стратэгічнае пашырэнне рынку",
          details: ["Аналіз рынку і картаванне магчымасцей", "Стратэгія выхаду на рынак", "Аптымізацыя працэсу продажаў", "Ініцыятывы росту даходаў"],
        },
        {
          title: "Лічбавы Маркетынг",
          description: "Максімізацыя вашай лічбавай прысутнасці і ахопу кліентаў",
          details: ["Кіраванне шматканальнымі кампаніямі", "Стратэгія і стварэнне кантэнту", "Аналітыка і аптымізацыя эфектыўнасці", "Пазіцыянаванне брэнда і камунікацыя"],
        },
        {
          title: "Інтэрым Менеджмент",
          description: "Экспертнае кіраўніцтва, калі яно найбольш патрэбна",
          details: ["Ролі C-suite і вышэйшага кіраўніцтва", "Трансфармацыйнае лідэрства", "Антыкрызіснае кіраванне", "Перадача ведаў і развіццё каманды"],
        },
        {
          title: "Рашэнні AI",
          description: "Выкарыстанне AI для трансфармацыі бізнес-аперацый",
          details: ["Гатовыя рашэнні", "Рашэнні на заказ", "Інтэграцыя рашэнняў і сістэм"],
          hasButton: true,
        },
      ],
    },
    whyAgape: {
      title: "Чаму AGAPE?",
      subtitle: "Большасць агенцтваў прадае актыўнасць і пакідае вас з інструментам. AGAPE працуе на вынік у продажах і дастаўляе яго як поўны white-glove сэрвіс.",
      values: [
        {
          title: "Продажы, а не пустыя лічбы",
          description: "У адрозненне ад агенцтваў, якія справаздачна паказваюць прагляды, клікі і адпраўленыя паведамленні, AGAPE працуе на тое, што для вас найважнейшае — звычайна колькасць продажаў. Ніякай працы дзеля працы і фальшывай статыстыкі.",
        },
        {
          title: "White-glove сэрвіс, а не проста інструмент",
          description: "У адрозненне ад інструментаў, з якімі вы застаяцеся сам-насам, AGAPE кіруе для вас уласнай топавай сістэмай пошуку лідаў і дастаўляе вынік: кваліфікаваныя ліды і сустрэчы, а не яшчэ адзін акаўнт.",
        },
        {
          title: "Хуткасць і Гнуткасць",
          description: "У адрозненне ад найму, які займае месяцы, звычайна рухавік продажаў запускаецца за 14–28 дзён: ад падрыхтоўкі базы і паведамленняў да бяспечнай налады дамена.",
        },
        {
          title: "Вымяральны Ўплыў",
          description: "Вымяраем канкрэтныя вынікі: KubaDrukuje атрымала 12 заказаў з Instagram за тры месяцы.",
        },
        {
          title: "Стратэгічны Фокус",
          description: "Для AleksanovInvest мы ўдакладнілі персону і прапанову перад аптымізацыяй продажаў у LinkedIn; гэта прывяло да першай платнай здзелкі.",
        },
        {
          title: "Глабальны Ахоп",
          description: "Прыклад працы паміж культурамі: лічбавая платформа і рэгулярныя AI-майстэрні для беларускай дыяспары ў Кракаве.",
        },
      ],
    },
    howWeWork: {
      title: "Як Мы Працуем",
      subtitle: "Наша правераная 6-этапная метадалогія забяспечвае паслядоўныя вынікі і празрыстае супрацоўніцтва на кожным этапе.",
      cta: "Запісацца на бясплатнае 25-хвіліннае спатканне",
      ctaHeading: "Гатовы пачаць супрацоўніцтва з AGAPE?",
      steps: [
        {
          title: "Адкрыццё",
          shortText: "Адкрыццё — гэта 25-хвіліннае пачатковае спатканне для ацэнкі мэтаў, выклікаў і бягучай сітуацыі вашага бізнесу, каб вырашыць, ці падыходзіць вам супрацоўніцтва з AGAPE.",
          expandedText: "",
        },
        {
          title: "Глыбокі Аналіз",
          shortText: "Комплексная аналітычная сесія",
          expandedText: "Дэталёвая 60-90 хвілінная сесія, падчас якой мы паглыбляемся ў вашы канкрэтныя патрэбы і вызначаем ключавыя магчымасці для росту.",
        },
        {
          title: "Стратэгія",
          shortText: "Стварэнне індывідуальнай дарожнай карты",
          expandedText: "На аснове нашага аналізу мы распрацоўваем адаптаваную стратэгію з выразнымі этапамі, KPI і практычнымі рэкамендацыямі.",
        },
        {
          title: "Прапанова",
          shortText: "Празрыстыя ўмовы супрацоўніцтва",
          expandedText: "Вы атрымліваеце дэталёвую прапанову з аб'ёмам, тэрмінамі, вынікамі і інвестыцыямі. Мы верым у поўную празрыстасць.",
        },
        {
          title: "Рэалізацыя",
          shortText: "Практычная рэалізацыя",
          expandedText: "Наша каманда працуе разам з вашай, каб рэалізаваць стратэгію. Мы забяспечваем рэгулярныя абнаўленні і паслядоўны прагрэс.",
        },
        {
          title: "Вынікі",
          shortText: "Вымяральныя вынікі і рост",
          expandedText: "Мы вымяраем поспех праз канкрэтныя вынікі: рост даходаў, пашырэнне рынку, аперацыйную эфектыўнасць.",
        },
      ],
    },
    caseStudies: {
      title: "Кейсы",
      subtitle: "Рэальныя вынікі з рэальных партнёрстваў. Даведайцеся, як мы дапамагалі бізнесам ператвараць выклікі ў магчымасці.",
      asOf: "Усе даныя правераны на верасень 2026.",
      challenge: "Выклік",
      solution: "Рашэнне",
      keyValue: "Ключавая Каштоўнасць",
      learnMore: "Даведацца больш",
      cases: [
        {
          company: "WorkForce PRO",
          challenge: "WorkForce PRO патрабавалася прафесійная прысутнасць у інтэрнэце для прыцягнення B2B кліентаў.",
          solution: "AGAPE распрацавала камунікацыйную стратэгію, кіруе LinkedIn акаўнтам кампаніі і стварае рассылку.",
          keyValue: "Дзякуючы эмпатычнаму падыходу AGAPE пабудавала брэнд з нуля, з больш чым 400 падпісчыкамі і 40 публікацыямі.",
        },
        {
          company: "VisualCore",
          challenge: "Visual Core патрабавалася хуткае стварэнне MVP сістэмы на аснове кагнітыўнай архітэктуры, інтэграванай з Telegram.",
          solution: "AGAPE стварыла чат-бота з вектарнай базай дадзеных (RAG) і сістэму падпіскі за два тыдні.",
          keyValue: "Супрацоўніцтва прывяло да хуткага запуску продажаў і паслядоўнага выгляду брэнда на LinkedIn.",
        },
        {
          company: "KubaDrukuje",
          challenge: "Куба меў талент і страсць да 3D-друку, але не хапала кірунку, стратэгіі і паслядоўнай камунікацыі брэнда.",
          solution: "AGAPE дапамагло вызначыць місію брэнда, стратэгію і стыль камунікацыі. Мы стварылі план кантэнту і ўзялі на сябе маркетынг у Instagram.",
          keyValue: "Профіль KubaDrukuje у Instagram вырас на 313% за тры месяцы, дасягнуўшы 27 000 праглядаў і 12 заказаў дзякуючы структураванай маркетынгавай і кантэнт-стратэгіі AGAPE.",
        },
        {
          company: "Himtek",
          challenge: "HIMTEK, прамысловая кампанія, мела патрэбу ў павышэнні пазнавальнасці брэнда і генерацыі запытаў праз LinkedIn.",
          solution: "AGAPE запусціла карпаратыўны акаўнт, публікуючы два посты ў тыдзень і будуючы камунікацыю для B2B кліентаў.",
          keyValue: "Ужо пасля першага месяца былі распрацаваны рэкамендацыі па аптымізацыі кантэнту для канверсіі і стратэгія далейшага развіцця.",
        },
        {
          company: "DanGuitarra",
          challenge: "Настаўнік гітары мусіў павялічыць бачнасць і цікавасць да ўрокаў сярод мясцовых вучняў.",
          solution: "AGAPE распрацавала маркетынгавую стратэгію на аснове TikTok, падрыхтавала план кантэнту і заняла сябе відэамантажом.",
          keyValue: "Была створана поўная маркетынгавая і аперацыйная сістэма: кантэнт, кампанія TikTok, анлайн-бранюванне з аплатамі і SMS-паведамленнямі.",
        },
        {
          company: "Polesie United",
          challenge: "Polesie United пачыналася як каманда сяброў без стратэгіі, структуры і вядомасці.",
          solution: "AGAPE ўзяла на сябе комплексны маркетынг клуба - ад рэбрэндынгу да крамы з атрыбутыкай і стратэгіі кантэнту.",
          keyValue: "За адзін год Polesie United ператварылася ў паўнавартасны клуб з больш чым 1000 падпісчыкамі і першым спонсарам.",
        },
        {
          company: "American Food Production Company",
          challenge: "Кліент меў больш за 2000 файлаў у 20-гадовым архіве Google Drive без структуры і арганізацыі.",
          solution: "AGAPE поўнасцю аблічбавала рэсурсы, стварыўшы Company Operating System у Notion з выкарыстаннем Notion AI.",
          keyValue: "Вынікам стала цэнтральнае сховішча ведаў, якое ператварыла хаос у празрыстую экасістэму.",
        },
        {
          company: "Natalia Pietrów",
          challenge: "Манетызацыя высокаспецыялізаваных ведаў (біяхакінг) патрабавала пабудовы з нуля маштабаванай інфраструктуры продажаў і структураванай маркетынгавай стратэгіі, без хаосу і павелічэння аперацыйных выдаткаў творцы.",
          solution: "AGAPE укараніла поўную аперацыйную архітэктуру: ад вызначэння Brand Core і USP, праз праектаванне структуры прадукту, да тэхнічнай e-commerce інфраструктуры (лэндынг, дамен, аўтаматызацыя дыстрыбуцыі e-book і сувязь відэа з QR-кодамі).",
          keyValue: "Творца атрымаў аўтаматызаваную сістэму прыцягнення кліентаў і мог на 100% засяродзіцца на змесце. Ліквідацыя ручной працы забяспечыла стабільны, прадказальны працэс продажаў і стабільны паток лідаў для новага брэнда.",
        },
        {
          company: "AleksanovInvest",
          challenge: "AleksanovInvest – адукацыйны брэнд у сферы інвестыцый – не меў дакладна вызначанага брэнда, персоны кліента і паслядоўнай прапановы. Пачатковая камунікацыя была накіравана на няправільны сегмент, а маркетынгавы канал быў абраны няўдала. Сам спосаб камунікацыі быў малаэфектыўны – прыносіў нязначныя вынікі продажаў пры вялікіх намаганнях. Уладальнік таксама не быў упэўнены, як павінен выглядаць прадукт і каму яго рэальна прадаваць.",
          solution: "На працягу першых месяцаў супрацоўніцтва AGAPE правяла кліента праз структураваны стратэгічны працэс у штотыднёвым рытме: апісанне брэнда і персоны, шматразовае ітэраванне прапановы, адаптацыя камунікацыі да новага ICP і запуск і аптымізацыя кампаніі outreach у LinkedIn. Паралельна ўпарадкавалі сайт, прапанову кансультацый і фундамент продажаў.",
          keyValue: "Супрацоўніцтва дало Раману Аляксанаву поўную яснасць адносна таго, як павінен выглядаць яго прадукт, што рабіць, каб ён прадаваўся, і да каго звяртацца. Кампанія LinkedIn пашырыла нэтворк, дапамагла знайсці эфектыўныя outbound-паведамленні і прынесла канверсію ў першую платную здзелку – на аснове новай прапановы і паслядоўных асноў брэнда. Цяпер AGAPE падтрымлівае AleksanovInvest у прыцягненні кліентаў.",
        },
        {
          company: "Беларуская дыяспара ў Кракаве",
          challenge: "Супольнасць дыяспары мела патрэбу не толькі ў прафесійнай лічбавай інфраструктуры з нуля, але перш за ўсё ў выхадзе з тэхналагічнага выключэння. Не хапала цэнтральнага пункту камунікацыі і практычных ведаў, як удзельнікі могуць выкарыстоўваць штучны інтэлект для павышэння асабістай і бізнес-прадукцыйнасці.",
          solution: "AGAPE спраектавала і ўкараніла поўную вэб-архітэктуру (belkrakow.org) – ад канфігурацыі дамена да UI/UX. Паралельна Раман Лядак запусціў „Лабараторыю Будучыні” (The Future Lab) – цыклічную праграму варштатаў, якая навучае ўкараненню AI-інструментаў у штодзённых аперацыях і задачах.",
          keyValue: "Створана стабільная і неабслугоўваная лічбавая экасістэма для супольнасці. Кожны месяц члены супольнасці набываюць цвёрдыя кампетэнцыі падчас варштатаў, непасрэдна пераводзячы AI-інструменты ў аўтаматызацыю і аптымізацыю ўласнага бізнесу, будуючы глыбокі давер да кампетэнцый AGAPE.",
        },
        {
          company: "Spotless Polska",
          challenge: "Марцін (Вроцлаў) выходзіў на рынак з мыццём фотаэлектрычных панэляў і шкляных фасадаў без хіміі. Ён меў тэхналогію, але не меў маркі, сайта, прайса і рэкамендацый – трэба было праверыць попыт, а не прыгожую прэзентацыю.",
          solution: "AGAPE пабудавала марку з нуля (дамен, скрыня з прагрэвам рэпутацыі, сайт, лагатып і ўлётка), а затым сабрала базу каля 1000 фірмаў і правяла e-mail-кампаніі для 4 сегментаў: сонечныя фермы, аўтасалоны, склады і вытворчыя прадпрыемствы.",
          keyValue: "743 лісты дасягнулі 385 фірмаў і прынеслі 38 адказаў (9,9%) і 4 запытаў на прапанову. У кліента застаюцца 4 гатовыя кампаніі і база – дзеючы фундамент продажаў, а не аднаразовая акцыя.",
        },
      ],
    },
    resources: {
      title: "Рэсурсы і Ўвагі",
      subtitle: "Будзьце ў курсе нашых апошніх думак пра AI, лічбавую трансфармацыю і бізнес-стратэгію.",
      readOn: "Чытаць на LinkedIn",
      viewAll: "Глядзець Усё на LinkedIn",
      posts: [
        {
          title: "AI Аўтаматызацыя і Лічбавая Трансфармацыя",
          excerpt: "Як аўтаматызацыя на аснове AI паскарае лічбавую трансфармацыю ў розных галінах.",
        },
        {
          title: "Укараненне AI у Польскіх Кампаніях",
          excerpt: "Як польскія кампаніі выкарыстоўваюць штучны інтэлект для павышэння інавацыйнасці і эфектыўнасці.",
        },
        {
          title: "Партнёрства Чалавек-AI",
          excerpt: "Як чалавечая крэатыўнасць і сінергія з AI перавызначаюць прадуктыўнасць і супрацоўніцтва.",
        },
        {
          title: "Генератыўны AI і Крэатыўнасць",
          excerpt: "Развіццё генератыўнага AI у дызайне і інавацыях кантэнту.",
        },
        {
          title: "Гумар AI і Рэальныя Ўрокі",
          excerpt: "Лёгкі погляд на памылкі AI, якія раскрываюць глыбейшыя ісціны пра інавацыі.",
        },
        {
          title: "Трэнінг Лічбавай Гігіены AGAPE",
          excerpt: "Практычная серыя семінараў пра лічбавае дабрабыт і разумнае выкарыстанне тэхналогій.",
        },
      ],
    },
    contact: {
      title: "Пачнем Размову",
      subtitle: "Гатовы даведацца, як AGAPE можа дапамагчы трансфармаваць ваш бізнес? Запішыцеся на бясплатнае 25-хвіліннае спатканне.",
      bookCallTitle: "Запісацца на Бясплатнае Спатканне",
      bookCallDesc: "Заплануйце 25-хвіліннае спатканне, каб абмеркаваць патрэбы вашага бізнесу.",
      bookCallBtn: "Запісацца праз Calendly",
      contactTitle: "Кантактная Інфармацыя",
      followUs: "Сачыце за Намі",
    },
    aboutUs: {
      heroTitle: "Раскрываем Лічбавы Патэнцыял",
      heroSubtitle: "Мы дапамагаем бізнесам знайсці спакой, упэўненасць і рэальную каштоўнасць праз эмпатыю і тэхналагічнае майстэрства.",
      corePhilosophy: "Даступнае Ўмацаванне",
      corePhilosophyDesc1: "Пераадольваем каштоўнасныя і тэхналагічныя бар'еры, каб зрабіць інавацыі",
      corePhilosophyDesc2: "простымі і дасягальнымі.",
      founderJourney: {
        title: "Шлях Заснавальніка",
        subtitle: "Ад IBM да AGAPE",
        corporate: {
          period: "2020-2023",
          title: "Карпаратыўны Фундамент",
          company: "IBM Consulting",
          role: "Кансультант па Аўтаматызацыі",
          description: "Раман Ледак працаваў кансультантам па аўтаматызацыі ў IBM Consulting. Ён вёў дзясяткі праектаў і ацаніў сотні працэсаў для глабальных лідэраў фінансаў і лагістыкі."
        },
        commercial: {
          period: "2023-2025",
          title: "Камерцыйны Мост",
          company: "Marketing Automation Agency",
          role: "Delivery Director",
          description: "Як Delivery Director у агенцтве Marketing Automation, Раман пераключыў фокус з аперацыйных зберажэнняў на генерацыю даходаў, пачаўшы AI-маркетынг, які павялічыў прадуктыўнасць каманды на 40%."
        },
        breakthrough: {
          period: "2025",
          title: "Прарыў AGAPE",
          description: "Заснаваная, каб пераадолець разрыў у \"Пост-Наймальнай Эканоміцы\", дазваляючы МСП маштабаваць аперацыі і продажы без павелічэння штату."
        },
        evolution: {
          period: "2026",
          title: "Эвалюцыя да AGAPE AUTOMATION SYSTEMS",
          description: "Аб'яднанне кампетэнцый двух маладых інжынераў з камплементарнымі кампетэнцыямі і агульнымі каштоўнасцямі, якое дазваляе маштабаваць каштоўнасць."
        }
      },
      specializedEdge: {
        title: "Наша Перавага",
        subtitle: "Распазнаванне Ўзораў",
        diagnostic: {
          title: "Дыягнастычны Талент",
          description: "AGAPE выдатна дыягнастуе, дзе менавіта губляецца каштоўнасць у складаных аперацыях."
        },
        stability: {
          title: "Прамысловая Стабільнасць",
          description: "У адрозненне ад \"хайп-арыентаванага\" рынку AI, мы забяспечваем стабільную, надзейную аўтаматызацыю карпаратыўнага класа."
        },
        goal: {
          title: "Мэта",
          description: "Ствараць тэхналогію, якая знікае на заднім плане, дазваляючы вам засяродзіцца на вашай візіі."
        }
      },
      values: {
        title: "Каштоўнасці AGAPE",
        subtitle: "\"Найвышэйшая Форма Кахання\"",
        intro: "Мы перавызначаем кансалтынг праз прызму эмпатыі і вымяральнай каштоўнасці.",
        items: [
          {
            title: "Заснаваныя на Каштоўнасці",
            impact: "Кожнае дзеянне мусіць прыносіць вымяральную, колькасную каштоўнасць для вашага бізнесу."
          },
          {
            title: "Мудрэц-Інаватар",
            impact: "Мы спалучаем мудрасць правадніка з энергіяй стваральніка для вырашэння праблем."
          },
          {
            title: "Эмпатыя і AGAPE",
            impact: "Мы разумеем, што бізнес-праблемы выклікаюць асабісты стрэс; мы імкнемся прынесці вам спакой."
          },
          {
            title: "Партнёрства",
            impact: "Мы не проста пастаўляем; мы будуем унутраныя кампетэнцыі і ўпэўненасць вашай каманды."
          }
        ]
      },
      credibility: {
        title: "Чаму Нам Давяраць?",
        subtitle: "Слупы Даверу",
        pillars: [
          {
            title: "Даказаныя Вынікі",
            description: "Мільёны зэканомлены для глабальных лідэраў і сотні аптымізаваных працэсаў."
          },
          {
            title: "Стратэгія Агенцтва, Цана МСП",
            description: "Стратэгія карпаратыўнага ўзроўню па цане, даступнай для амбіцыйных бізнесаў."
          },
          {
            title: "Маладыя і Адкрытыя",
            description: "Каманда, якая кіруецца крэатыўным мысленнем і нестандартным вырашэннем праблем."
          }
        ]
      },
      cta: {
        title: "Гатовы да прарыву?",
        subtitle: "Паміж тым, дзе вы ёсць, і тым, дзе хочаце быць?",
        button: "Запісацца на Бясплатнае 25-Хвіліннае Спатканне"
      }
    },
    personaFi: {
      hero: {
        tagline: "AGAPE • AI на Аснове Каштоўнасці для Маркетолагаў",
        title: "Праверце свой маркетынг за",
        titleHighlight1: "2 хвіліны",
        titleConnector: "за",
        titleHighlight2: "€1",
        description: "Перастаньце гадаць, ці спрацуюць вашы персоны і камунікацыя. Атрымайце валідацыю на аснове фрэймворкаў за 2 хвіліны.",
        founderLabel: "Праверана Заснавальнікам, Даказаныя Вынікі:",
        founderQuote: "\"Нягледзячы на тое, што ў мяне салідная экспертыза ў маркетынгу - мне не хапала перспектыў. PersonaFi даў 360-градусныя дадзеныя.\"",
        founderName: "- Раман Ледак, Заснавальнік AGAPE, Кракаў",
        statReply: "+158% каэф. адказаў",
        statVisits: "+100% наведванняў сайта",
        statConversion: "+30% канверсія",
        ctaTry: "Паспрабаваць Бясплатна (30 сек)",
        ctaSample: "Глядзець Прыклад Справаздачы",
        trustAccuracy: "Мінімум 95% дакладнасці",
        trustFree: "3 запускі БЯСПЛАТНА",
        trustRefund: "Поўная гарантыя вяртання",
        trustData: "Мы не выкарыстоўваем вашы дадзеныя",
        versionNote: "PersonaFi v1.0 · стан на верасень 2026",
      },
      stats: {
        title: "У Лічбах",
        founderTested: "ПРАВЕРАНА",
        founderTestedDesc: "Рэальная валідацыя, рэальныя вынікі",
        replyRate: "+158%",
        replyRateDesc: "Паляпшэнне каэф. адказаў (12%→31%)",
        accuracy: "≥95%",
        accuracyDesc: "Дакладнасць пацверджана прафесіяналамі",
        price: "€1",
        priceDesc: "За валідацыю - танней за каву",
        tagline: "Рэальная валідацыя. Рэальны ўплыў. Рэальныя маркетолагі.",
        asOf: "Даныя на верасень 2026.",
      },
      beforeAfter: {
        title: "Глядзіце PersonaFi ў Дзеянні",
        subtitle: "Рэальныя прыклады, як PersonaFi трансфармуе маркетынгавыя вынікі.",
        example1Title: "Рэальны Прыклад: Валідацыя Outreach Кампаніі",
        example1Subtitle: "Раман Ледак (Заснавальнік AGAPE) меў 12% каэф. адказаў. Вось што выявіў PersonaFi:",
        beforeTitle: "Да PersonaFi",
        beforeText: "\"Прывітанне [Імя], я дапамагаю бізнесам паляпшаць маркетынг з AI. Ці зацікавіла б вас?\"",
        beforeResult: "Вынік: 12% каэф. адказаў",
        identifiedTitle: "Што Выявіў PersonaFi",
        identifiedScore: "Ацэнка Адпаведнасці Паведамленне-Персона:",
        identifiedItems: ["Няма канкрэтнай праблемы кліента", "\"Паляпшэнне маркетынгу з AI\" занадта агульнае", "Няма трыгера пакупкі", "CTA мяркуе цікавасць"],
        afterTitle: "Пасля Рэвізіі PersonaFi",
        afterText: "\"Прывітанне [Імя], бачыў, што вы запускаеце [новую паслугу]. Большасць заснавальнікаў марнуюць €500-€2K на тэставанне камунікацыі...\"",
        afterReply: "31% каэф. адказаў (+158%)",
        afterVisits: "+100% наведванняў сайта",
        afterConversion: "+30% канверсія",
        realExample: "ГЭТА РЭАЛЬНЫ ПРЫКЛАД ад заснавальніка PersonaFi.",
        example2Title: "Валідацыя Кантэнту Social Media",
        example2Subtitle: "Што ўвёў менеджар social media:",
        example2Input: "Персона: \"Карыстальнікі Instagram 25-35, зацікаўленыя ў ўстойлівай модзе\" | Кантэнт: Карусель Instagram \"10 Спосабаў Стварыць Капсульную Гардэробу\"",
        example2Score: "Ацэнка Адпаведнасці Кантэнт-Персона:",
        example2ScoreLabel: "3/5 (Умеранае)",
        example2WhyTitle: "Чаму гэта не трапляе:",
        example2WhyItems: ["\"Капсульная гардэроба\" ≠ устойлівасць", "Няма ўстойлівых брэндаў ці матэрыялаў", "Няма экалагічнага кута", "\"10 спосабаў\" навучае, але не ўцягвае"],
        example2StrongerTitle: "Мацнейшыя падыходы да кантэнту:",
        example2StrongerItems: ["\"10 Устойлівых Брэндаў для Вашай Капсульнай Гардэробы\"", "\"Як Я Стварыла Капсульную Гардэробу Без Fast Fashion\"", "\"Экалагічны Ўплыў: Гардэроба з 10 vs 100 Рэчаў\""],
        example2TipsTitle: "Парады для Instagram:",
        example2TipsItems: ["Дадайце апытанне: \"Які ўстойлівы брэнд наступны?\"", "UGC кут: Папрасіце падпісчыкаў паказаць гардэроб"],
        ctaTryFree: "Паспрабаваць Бясплатную Валідацыю",
      },
      accuracy: {
        title: "Як Мы Вымяраем \"Мінімум 95% Дакладнасці\"",
        subtitle: "PersonaFi дасягае мінімум 95% дакладнасці ў параўнанні з вопытнымі прафесіяналамі маркетынгу.",
        criteriaIntro: "Мы параўноўваем справаздачы PersonaFi з ацэнкамі стратэгаў (10+ гадоў) па 6 крытэрыях:",
        criteria: ["Яснасць і паўната ICP", "Дакладнасць праблем кліента", "Рэзананс камунікацыі", "Апрацоўка пярэчанняў", "Канкурэнтнае пазіцыянаванне", "Псіхалагічныя трыгеры"],
        whyTitle: "Чаму \"мінімум\"?",
        whyText: "У многіх выпадках PersonaFi выяўляе прагалы, якія нават вопытныя маркетолагі прапускаюць. Мы ўсталявалі 95% як мінімум - фактычнае супадзенне часта перавышае гэта.",
        conclusion: "Аналіз прафесійнага ўзроўню без наймання прафесіяналаў.",
      },
      howItWorks: {
        title: "Як Працуе PersonaFi",
        subtitle: "4 простыя крокі. 2 хвіліны. Нуль тэхнічных навыкаў.",
        steps: [
          { title: "Устаўце Свой Кантэнт", desc: "Падзяліцеся тым, што хочаце праверыць: каштоўнасную прапанову, email, landing page, social media.", example: "Звычайны тэкст - без спецыяльнага фарматавання." },
          { title: "Апішыце Свайго Кліента", desc: "Увядзіце 2-3 сказы пра тое, на каго вы кіруецеся.", example: "\"Уладальнікі малога бізнесу, якія маюць праблемы з маркетынгам і не маюць бюджэту на агенцтвы\"" },
          { title: "Атрымайце Імгненную Валідацыю", desc: "Атрымайце структураваную справаздачу з ацэнкамі і канкрэтнымі рэкамендацыямі.", example: "Справаздача з ацэнкамі і рэкамендацыямі." },
          { title: "Ітэруйце або Запускайце", desc: "Выкарыстайце зваротную сувязь для ітэрацыі або ў сваіх прэзентацыях.", example: "Гэтая цана дазваляе правесці мноства ітэрацый." },
        ],
        cta: "Пачаць Бясплатную Валідацыю",
      },
      whoItsFor: {
        title: "Створана Для Маркетолагаў, Якія Не Могуць Сабе Дазволіць Гадаць",
        personas: [
          { title: "Сола-Прадпрымальнікі і Малыя Каманды", benefits: ["Праверце камунікацыю перад расходамі на рэкламу", "Атрымайце незалежную другую думку", "Запускайце з упэўненасцю"] },
          { title: "Менеджары Кантэнту і Маркетынгу", benefits: ["Пратэстуйце некалькі кутоў кампаніі за адно папалудне", "Будуйце рашэнні на аснове дадзеных", "Прыярытызуйце кантэнт, які рэзаніруе з вашым ICP"] },
          { title: "Маркетолагі Social Media", benefits: ["Хутка правярайце канцэпцыі social media", "Параўнайце некалькі падыходаў", "Аптымізуйце хукі і CTA перад публікацыяй"] },
          { title: "Маркетынгавыя Агенцтвы", benefits: ["Выстаўляйце кліентам €500 за аналіз, які каштуе €1", "White-label справаздачы як уласную метадалогію", "Маштабуйце інсайты для ўсіх кліентаў"] },
          { title: "Enterprise Каманды (10+)", benefits: ["Правярайце 8+ сегментаў за менш чым 2 дні", "Інтэгруйце інсайты з CRM", "Выраўняйце крос-функцыянальныя каманды"] },
        ],
      },
      testimonial: {
        title: "Рэальныя Вынікі Карыстальнікаў PersonaFi",
        quote: "\"Пасля таго як я пачаў выкарыстоўваць PersonaFi для валідацыі маіх кампаній, вынікі былі ашаламляльныя. PersonaFi паказаў мне дакладна, чаго не хапала ў маёй камунікацыі.\"",
        badgeReply: "+158% каэф. адказаў",
        badgeVisits: "+100% наведванняў сайта",
        badgeConversion: "+30% канверсія",
        authorName: "Раман Ледак, Заснавальнік",
        authorCompany: "AGAPE • Кракаў, Польшча",
        invitation: "PersonaFi толькі запусціўся. Калі ён дапаможа вам палепшыць маркетынг, мы з задавальненнем прадставім вашы вынікі.",
        invitationCta: "Паспрабуйце Бясплатную Валідацыю - Станьце Часткай Нашых Гісторый Поспеху",
      },
      comparison: {
        title: "Як PersonaFi Параўноўваецца з Альтэрнатывамі",
        headers: ["Атрыбут", "Агенцтвы", "ChatGPT", "Уласнае Даследаванне", "PersonaFi ⭐"],
        rows: [
          ["Дакладнасць", "Высокая", "Пераменная", "Пераменная", "≥95% (пацверджана)"],
          ["Кошт", "€3K-€10K", "€20/мес.", "\"Бясплатна\" (час)", "€1 за валідацыю"],
          ["Час", "1-2 тыдні", "10-30 хв", "Дні-тыдні", "2 хвіліны"],
          ["Паслядоўнасць", "Высокая", "Нізкая", "Нізкая", "Высокая (≥95%)"],
          ["Патрэбныя навыкі", "Няма", "Высокія", "Высокія", "Няма"],
          ["Аплата", "Рэтэйнер", "Падпіска", "Кошт часу", "Pay-per-use"],
          ["Ітэрацыі", "Абмежаваныя", "Неабмежаваныя", "Неабмежаваныя", "Неабмежаваныя (€1)"],
        ],
        footer: "PersonaFi - адзінае рашэнне, якое спалучае прафесійную дакладнасць + імгненную хуткасць + радыкальную даступнасць + нуль бар'ераў.",
      },
      agencyDiff: {
        title: "Як Вылучыцца Без Страты Канкурэнтнай Перавагі",
        subtitle: "\"Калі кожная агенцтва можа гэта white-labelаваць, як мне вылучыцца?\"",
        answer: "Адказ: PersonaFi - гэта інфраструктура, не стратэгія.",
        diffTitle: "Ваша Вылучэнне Паходзіць з:",
        diffs: [
          { bold: "Галіновая экспертыза", text: "Вы разумееце канкурэнтны ландшафт вашага кліента" },
          { bold: "Стратэгічная інтэрпрэтацыя", text: "Вы пераўтвараеце валідацыю ў выканальныя кампаніі" },
          { bold: "Адносіны з кліентамі", text: "У вас ёсць кантэкст, якога PersonaFi ніколі не будзе мець" },
          { bold: "Рэалізацыя", text: "Вы рэалізуеце стратэгію - PersonaFi толькі валідуе" },
          { bold: "Уласныя фрэймворкі", text: "Накладзіце сваю метадалогію на PersonaFi" },
        ],
        guaranteeTitle: "Гарантыя Якасці:",
        guaranteeText: "Калі кліент незадаволены якасцю аналізу, мы выправім бясплатна або вернем грошы. Ваша рэпутацыя - наш прыярытэт.",
        cta: "Запісацца на Дэма для Агенцтва",
      },
      pricing: {
        title: "Простая, Сумленная Цана",
        payPerUse: "Pay-Per-Use",
        price: "€1",
        perValidation: "за валідацыю",
        noSubs: "Без падпісак. Без абавязацельстваў. Без схаваных плацяжоў.",
        items: ["Першыя 3 валідацыі: БЯСПЛАТНА", "95%+ гарантыя дакладнасці", "Поўнае вяртанне пры незадаволенасці", "Без крэдытнай карты на старце"],
        cta: "Пачаць Бясплатную Валідацыю",
        enterpriseTitle: "Цэны Enterprise:",
        enterpriseDesc: "500-1000 валідацый/год: €0.60-€0.80 за валідацыю • Уласнае API • Выдзеленая падтрымка",
        comparePersonaFi: "PersonaFi",
        comparePersonaFiPrice: "€10",
        comparePersonaFiDesc: "10 валідацый",
        compareAgency: "Агенцтва",
        compareAgencyPrice: "€5K-€10K",
        compareAgencyDesc: "1 праект",
        compareChatGPT: "ChatGPT Plus",
        compareChatGPTPrice: "€20/мес.",
        compareChatGPTDesc: "неабмежавана (пераменна)",
      },
      faq: {
        title: "Частыя Пытанні",
        items: [
          { q: "Ці працуе PersonaFi?", a: "Так. Раман Ледак выкарыстаў яго ў сваіх кампаніях: +158% каэф. адказаў, +100% наведванняў сайта, +30% канверсія. Першыя 3 валідацыі БЯСПЛАТНА." },
          { q: "Што значыць \"мінімум 95% дакладнасці\"?", a: "Справаздачы PersonaFi адпавядаюць або перавышаюць ацэнкі прафесійных стратэгаў мінімум у 95% выпадках па 6 крытэрыях." },
          { q: "Я ўжо карыстаюся AI інструментамі. Навошта мне PersonaFi?", a: "Нават заснавальнік AGAPE знайшоў прагалы, якія прапусціў. Агульны AI дапамагае ствараць кантэнт. PersonaFi забяспечвае валідацыю па 6 крытэрыях." },
          { q: "А калі я скептычна стаўлюся да вынікаў?", a: "Здаровы скептыцызм - гэта добра! Вынікі Рамана рэальныя. Першыя 3 валідацыі БЯСПЛАТНА. Поўнае вяртанне за аплачаныя валідацыі." },
          { q: "Чым гэта адрозніваецца ад ChatGPT?", a: "ChatGPT - агульнага прызначэння. PersonaFi створаны мэтанакіравана: простая мова, 95%+ дакладнасць, структураваныя справаздачы, 2 хвіліны." },
          { q: "Якія тыпы кантэнту я магу правяраць?", a: "Усе маркетынгавыя кантэнт: каштоўнасныя прапановы, скрыпты продажаў, email кампаніі, landing page, social media, ICP дэфініцыі." },
          { q: "Ці бяспечныя мае дадзеныя?", a: "Так. Кантэнт аналізуецца, але ніколі не захоўваецца назаўжды. Нуль захоўвання дадзеных. GDPR сумяшчальнасць." },
          { q: "Ці магу я выкарыстоўваць гэта для кліентаў агенцтва?", a: "Абсалютна. White-label як уласная метадалогія. Выстаўляйце кліентам €500 за аналіз, які каштуе €1." },
        ],
      },
      finalCta: {
        title: "Паспрабуйце Бясплатна Зараз",
        subtitle: "Для сола-прадпрымальнікаў, кантэнт-маркетолагаў, менеджараў social media, агенцтваў і enterprise камандаў",
        description: "Перш чым выдаткаваць €500-€2 000 на наступную кампанію, інвестуйце 2 хвіліны, каб праверыць, ці маюць сэнс вашы персоны і камунікацыя.",
        bullets: ["6 ацэнак, якія паказваюць моцныя і слабыя бакі", "Канкрэтныя прагалы ў вашай персоне/кантэнце", "Рэкамендацыі для капіравання на сёння", "Упэўненасць для запуску або ітэрацыі"],
        timeNote: "2 хвіліны • Першыя 3 валідацыі БЯСПЛАТНА",
        cta: "Пачаць Бясплатную Валідацыю",
        trustAccuracy: "≥95% дакладнасці",
        trustFree: "Першыя 3 валідацыі БЯСПЛАТНА",
        trustRefund: "Поўная гарантыя вяртання",
        trustData: "Мы не выкарыстоўваем вашы дадзеныя",
        tagline: "AGAPE PersonaFi - частка экасістэмы кансалтынгу AGAPE, заснаванай на каштоўнасці.",
      },
    },
    blog: {
      heroTitle: "Блог AGAPE",
      heroSubtitle: "Практычныя плэйбукі па AI, продажах і аперацыях для МСП, якія маштабуюцца без павелічэння каманды.",
      allCategories: "Усе",
      read: "Чытаць",
      minutes: "хв чытання",
      empty: "Пакуль няма артыкулаў - неўзабаве з'явіцца.",
      back: "Вярнуцца ў блог",
      published: "Апублікавана",
      author: "Аўтар",
      related: "Можа таксама зацікавіць",
      fallbackNotice: "Даступна па-англійску",
      updatedOn: "Абноўлена",
      readInEnglish: "Чытаць па-англійску",
      externalBadge: "LinkedIn",
      searchPlaceholder: "Шукаць артыкулы...",
      searchEmpty: "Ніводны артыкул не адпавядае запыту.",
      externalTooltip: "Адкрываецца на LinkedIn у новай укладцы",
      notFoundTitle: "Артыкул не знойдзены",
      notFoundSubtitle: "Гэты артыкул не існуе або яшчэ не апублікаваны.",
      newsletter: {
        title: "Будзьце ў курсе",
        description: "Атрымлівайце нашы новыя артыкулы і правераныя ў баі фрэймворкі прама на скрыню. Без спаму, без вады.",
        placeholder: "vash@email.com",
        subscribe: "Запісацца",
        success: "Дзякуй! Вы ў спісе.",
        invalidEmail: "Увядзіце правільны e-mail.",
        error: "Нешта пайшло не так. Паспрабуйце яшчэ раз.",
      },
      stickyCta: {
        ariaLabel: "Застанемся ў кантакце",
        title: "Спадабаўся артыкул?",
        description: "Атрымайце наступны на сваю скрыню.",
        bookCall: "Замовіць 25-хв размову",
        bookCallShort: "Замовіць размову",
        dismiss: "Закрыць",
      },
      faqTitle: "Часта задаваныя пытанні",
      libertyNotePre: "Артыкул створаны з дапамогай уласнай кантэнт-машыны AGAPE — ",
      libertyNoteLink: "AGAPE Liberty",
      libertyNotePost: ".",
    },
    personaFiPopup: {
      badge: "Бясплатна",
      title: "Атрымайце зваротную сувязь, якая дапаможа вашаму кантэнту прадаваць больш",
      description:
        "З PersonaFi вы бясплатна атрымліваеце зваротную сувязь пра свой кантэнт, каб палепшыць яго і прадаваць больш — без рэгістрацыі, без карты.",
      cta: "Паглядзець PersonaFi",
      later: "Магчыма пазней",
      dismiss: "Закрыць",
    },
    footer: {
      rights: "Усе правы абаронены.",
      lastUpdated: "Апошняе абнаўленне сайта",
      privacyPolicy: "Палітыка Прыватнасці",
      cookiePolicy: "Палітыка Cookies",
    },
  },
} as const;

export type Translations = typeof translations.en;
